/**
 * Sketch Object Tool (Layout persona)
 *
 * Places and edits SKETCH_OBJECT annotations — furniture/fixture symbols
 * (bath, chair, cabinet, …) from the sketch-object type registry. Behaviour:
 *  - The tool menu hosts a scrollable, searchable LIBRARY of object types
 *    (grouped by category); it auto-opens when the tool activates. Clicking a
 *    card arms that type for placement.
 *  - Armed: a ghost glyph follows the cursor, resolved by the type's placement
 *    constraint. FREE types follow the floor plane but auto-ALIGN to a wall
 *    when the cursor is near one (closest side flush against the face at the
 *    nearest quarter-turn, clamped inside the wall span so it never sticks
 *    through a corner); tapping `R` rotates a free ghost by 45° (holding it
 *    rotates smoothly; a plain 2D scroll rotates in fine 5° notches — wall
 *    types and the zoom gestures (ctrl+scroll in 2D, any scroll in 3D) are
 *    never captured) — and against a wall
 *    picks which side attaches. WALL + POINT types (toilet) snap
 *    flush to the nearest wall face and place with one click. LINE-anchored
 *    types are RUNS drawn with the opening tool's UX while on a wall: a
 *    marker snaps onto the nearest wall edge; click-click OR click-drag
 *    rubber-bands a warm span line and commits the run — the parametric
 *    glyph skins the drawn length. WALL runs (cabinets, radiators) attach
 *    parametrically and only place on walls; FREE runs (stairs) are HYBRID —
 *    span-drawn when on a wall (aligned, stored as a free anchor), stamped
 *    like any free object elsewhere. `drawLikeWall` FREE runs (beams) use
 *    the wall-editor draw UX EVERYWHERE: click-click or drag between any two
 *    points, each endpoint snapping onto a nearby wall centerline (smart
 *    connect); holding Shift or Ctrl suppresses the beam's wall/90° snaps,
 *    while drawing and during every later handle/body drag. ESC or
 *    right-click cancels the run, then disarms.
 *  - Idle: hover an existing object → move cursor; click selects; drag moves it
 *    (constraint-aware — wall objects slide along their wall, free objects get
 *    smart-guide alignment snapping against their peers); tapping `R` rotates
 *    the selected free objects by 45°, holding it rotates them smoothly;
 *    right-click on an object opens a context
 *    menu (delete), right-click on empty space deselects; Delete removes the
 *    selection. A selected RUN (LINE-anchored: cabinets on their wall, stairs
 *    anywhere) shows double-arrow RESIZE handles at both ends — drag one to
 *    grow/shrink the run (the far end stays pinned, module skinning reflows
 *    live).
 *  - Undo/redo covers place, move, rotate, resize and delete.
 */

import * as THREE from 'three';
import { v4 as uuid } from 'uuid';
import { Line2 } from 'three/examples/jsm/lines/Line2.js';
import { LineGeometry } from 'three/examples/jsm/lines/LineGeometry.js';
import { LineSegments2 } from 'three/examples/jsm/lines/LineSegments2.js';
import { LineSegmentsGeometry } from 'three/examples/jsm/lines/LineSegmentsGeometry.js';
import { LineMaterial } from 'three/examples/jsm/lines/LineMaterial.js';
import { FloorplanToolBase } from './FloorplanToolBase';
import { Viewer } from '../../../viewer/viewer';
import { IPersona, IViewConfig } from '../../types';
import { ViewModes } from '../../helpers';
import { CameraMode } from '../../../defines';
import { ToolSettingsSchema, ToolSettingsChangeEvent } from '../../ToolSettings';
import { DragEventData } from '../../BaseTool';
import { Snapshot } from '../ToolUndoRedoManager';
import { PointoramaWallSketch } from '../../../utils/PointoramaWallSketch';
import { PixelHitTester } from '../modules/selection/PixelHitTester';
import { OpeningFlipGizmoInteraction } from '../modules/openingGizmos/OpeningFlipGizmos';
import { RoomEdgePick, collectRoomEdges, collectOutlineEdges, materializeWallEdgeFromRoomPick, persistWallSketch, pickRoomEdgeAtPixel, removeLooseWallEdge } from '../modules/RoomEdgeFallback';
import { TransformGizmoInteraction } from '../modules/transformGizmo/TransformGizmoInteraction';
import { SketchObjectGizmoBinding } from '../modules/transformGizmo/SketchObjectGizmoBinding';
import { capturePlacementValues } from '../../../sketchObjects/constraints/placementCapture';
import {
  SelectedObjectDragController,
  createPlacementMoveSnapshot,
  gizmoContrastColor,
  snapOverridden,
  snapSegmentTo90,
  snapFreeRunPointInfo,
  snapDimensionPointInfo,
  skinSidesAt,
  type FreeRunPointInfo,
  intersectRayWithEdge,
  cursorOffsetOnEdge,
  MIN_RUN_LENGTH,
  type ResizeHandleEnd,
} from '../modules/sketchObjects/SelectedObjectDrag';
import { squareDimensionRun } from '../modules/sketchObjects/dimensionSquare';
import { PointoramaOutline } from '../../../utils/PointoramaOutline';
import { BoxSelectController, BoxSelectMode, BoxRect } from '../modules/selection/BoxSelectController';
import { ContextMenu, ICON_TRASH_SVG } from '../modules/contextMenu/ContextMenu';
import { PointoramaSketchObject, pickSketchObjectAtPixel } from '../../../utils/PointoramaSketchObject';
import { isAnnotationPickBlocked } from '../../../viewer/AnnotationBlacklistStack';
import { getSketchObjectLibrary, getSketchObjectLibraryHost, getSketchObjectType, onSketchObjectTypesChanged, type RegisteredSketchObjectType, type SketchObjectLibraryKind } from '../../../sketchObjects/registry';
import { SketchObjectAnchorKind, type SketchObjectFrame } from '../../../sketchObjects/types';
import { defaultParamValues, paramPropertyIdentifier } from '../../../sketchObjects/paramSchema';
import { DIMENSION_OFFSET_PARAM } from '../../../sketchObjects/custom/customSketchObjectType';
import type { CustomParamValues } from '../../../sketchObjects/custom/evaluateCustomGlyph';
import { getConstraintStrategy } from '../../../sketchObjects/constraints/constraintRegistry';
import { resolveWallSpan } from '../../../sketchObjects/constraints/WallConstraint';
import { getStickyAlignedEdgeId, resolveFreeRun, resolveFreeWallSpan } from '../../../sketchObjects/constraints/FreeConstraint';
import type { AlignGuide } from '../../../sketchObjects/constraints/alignSnap';
import type { PlacementContext, PlacementValues, ResolvedPlacement, SketchObjectRequest } from '../../../sketchObjects/constraints/types';
import { projectMouseToFloor, snapToWallEdge, SKETCH_OBJECT_WALL_SNAP, type WallEdgeHit } from './WallSketchSnapEngine';
import { DEFAULT_DIMENSION_TYPE_ID, OpeningKind, type CustomOpeningTypeId } from 'shared-types';
import { getCustomOpeningType, offeredCustomOpeningTypes } from '../../../sketchObjects/custom/openings/customOpeningRegistry';
import {
  OpeningEdgeHit,
  OPENING_EDGE_RADIUS_PX,
  OPENING_NODE_SNAP_RADIUS_PX,
  MIN_OPENING_LENGTH,
  OpeningPlacementSettings,
  openingClearSpanOnEdge,
  clampOpeningOffsetToClearSpan,
  openingSpanPolyline,
  openingFootprint,
  pickOpeningSketchAtPixel,
  cursorOffsetOnOpeningEdge,
  loadOpeningSettingsForKind,
  buildOpeningSketchRequest,
  captureOpeningSketchRequest,
  createOpeningToggleSnapshot,
  OpeningFootprintHighlight,
  buildOpeningLibraryThumbnail,
} from '../modules/openingSketch/OpeningSketchShared';
import { PointoramaOpeningSketch } from '../../../utils/PointoramaOpeningSketch';
import {
  getOpeningSketchFixedLengthProperty,
  getOpeningSketchDefaultLengthProperty,
  getOpeningSketchLowerBoundProperty as getUserOpeningLowerBoundProperty,
  getOpeningSketchUpperBoundProperty as getUserOpeningUpperBoundProperty,
} from '../../../../modules/Properties/integration/UserSettingsIntegration';
import { pointAtArcLength } from '../../../utils/skeleton/arcGeometry';
import { remapHitToEdgeLine, wallEdgeGeometry, type WallEdgeGeometry } from '../../../sketchObjects/constraints/WallConstraint';
import { SphereMarker } from '../../../utils/SphereMarker';
import { applyBackdropInvert } from '../../../utils/BackdropContrast';
import {
  PropertyType,
  PropertyValueType,
  getGlobalPropertyService,
  type Coordinate,
  getSketchObjectWidthProperty,
  getSketchObjectDepthProperty,
} from '@pointorama/potree-shared';
import { t } from '../../../translation/markers';

const GLYPH_PICK_RADIUS_PX = 10;
// Matches the placed glyph's default `sketchobject_color` (black) so the
// preview looks like the object that will be placed. The ghost LINE gets the
// same backdrop-invert shader as placed default-black glyphs; this solid color
// remains for the (non-inverting) hover sphere.
const GHOST_COLOR = new THREE.Color(0x000000);
const GHOST_LINE_WIDTH = 3;
const GHOST_RENDER_ORDER = 999;
/** The run span being drawn over the wall edge — the same warm color the
 *  opening draw tool uses, so drawing feels identical across both tools. */
const DRAW_LINE_COLOR = new THREE.Color(0xffaa00);
const DRAW_LINE_WIDTH = 6;
/** Rotation step of a `R` key tap while placing a free object. */
const ROTATE_STEP = Math.PI / 4;

/** Continuous rotation speed (rad/s) while `R` is held down. */
const ROTATE_HOLD_SPEED = Math.PI / 2;
/** Rotation per wheel notch while a placement ghost is armed — finer than the
 *  45° tap step so scrolling gives precise control. */
const ROTATE_WHEEL_STEP = Math.PI / 36;
/** Smart-guide alignment lines (peer-object snapping of free placements). */
const GUIDE_COLOR = new THREE.Color(0xff4fd8);
const GUIDE_LINE_WIDTH = 1.5;
/** Tool-menu key prefix of the next dimensions' own options (`dimensionParam:<param key>`). */
const DIMENSION_PARAM_SETTING = 'dimensionParam:';



export class SketchObjectTool extends FloorplanToolBase {
  readonly name: string;
  /**
   * Which library the tool places from: the objects (plus doors & windows),
   * or the dimension annotations (the design tab's dimension tool, armed with
   * the default dimension on activation, endpoints snapping to the wall skin).
   */
  private readonly libraryKind: SketchObjectLibraryKind;

  private hitTester: PixelHitTester;
  /** Door/window flip gizmos for a single selected opening (shared UX). */
  private flipInteraction: OpeningFlipGizmoInteraction;

  /** The armed library type id, or null when idle. */
  private armedTypeId: string | null = null;
  /**
   * Armed door/window placement (the library's "Doors & windows" category,
   * item values `opening:door` / `opening:window`). Mutually exclusive with
   * `armedTypeId` — openings place OPENING_SKETCH annotations on the wall
   * sketch centerline, mirroring the OpeningSketchDrawTool's UX.
   */
  private armedOpening: { kind: OpeningKind; customType: CustomOpeningTypeId | null } | null = null;
  /** Per-user per-kind opening placement settings for the armed kind. */
  private openingSettings: OpeningPlacementSettings = loadOpeningSettingsForKind(OpeningKind.DOOR);
  /** First click of a free-drawn opening span, awaiting the second click. */
  private pendingOpeningStart: OpeningEdgeHit | null = null;
  /** Drag-to-draw start for an opening span (mousedown over a wall). */
  private openingDrawDrag: OpeningEdgeHit | null = null;
  /** Footprint highlight over selected openings (a slide's live highlight is
   *  owned by the drag controller). */
  private openingHighlight = new OpeningFootprintHighlight(this.viewer, () => this.ensureOverlay());

  /** True while ANY library item (object type or opening) is armed. */
  private get armed(): boolean {
    return this.armedTypeId !== null || this.armedOpening !== null;
  }
  /** Pending placement rotation (radians) for free objects, driven by `R`. */
  private rotation = 0;
  /** Last hover event, so rotation changes can re-resolve the ghost in place. */
  private lastHoverEvent: MouseEvent | null = null;
  /** First click of a wall RUN (LINE-anchored WALL types), held until the
   *  second click on the same wall commits the span. */
  private pendingRunStart: WallEdgeHit | null = null;
  /** First point of a free two-point run (`drawLikeWall` types — beams), held
   *  until the second click commits. Endpoints snap onto nearby walls. */
  private pendingFreeRunStart: THREE.Vector3 | null = null;
  /** Tangent of the wall the free run STARTED on (null when it started in
   *  free space) — the reference axis for the beam's 90° direction snap. */
  private freeRunStartDir: { x: number; y: number } | null = null;
  /** A dimension's two measured points, awaiting the third click that pulls
   *  its line off them (the offset). */
  private pendingDimension: { start: THREE.Vector3; end: THREE.Vector3 } | null = null;
  /** The current press picked a whole wall face for a dimension (its two ends):
   *  a drag places the line on release, a plain click then waits for the offset
   *  click. Set on every press, so it never outlives its own (trailing) click. */
  private faceDimensionPress = false;
  /** The next dimensions' own options per dimension type (the tool menu), on top of the type defaults. */
  private nextDimensionParams = new Map<string, CustomParamValues>();
  /** The next dimensions' length text (the tool menu). */
  private nextDimensionText = { decimals: 2, showUnit: true, alongLine: true, size: 1 };

  // Ghost preview (added to uiScene so it draws on top in 2D).
  private overlay: THREE.Group | null = null;
  private ghostLine: LineSegments2 | null = null;
  private ghostGeometry: LineSegmentsGeometry | null = null;
  private ghostMaterial: LineMaterial | null = null;

  // Run-draw visuals, matching the opening draw tool: a snap marker on the
  // wall centerline plus a fat rubber-band span line while drawing.
  private hoverMarker: SphereMarker | null = null;
  private spanLine: Line2 | null = null;
  private spanGeometry: LineGeometry | null = null;
  private spanMaterial: LineMaterial | null = null;

  // Smart-guide lines shown while a free placement/drag is snapped to a peer.
  private guideLine: LineSegments2 | null = null;
  private guideGeometry: LineSegmentsGeometry | null = null;
  private guideMaterial: LineMaterial | null = null;
  /** Drag-to-draw start for a wall run (captured on mousedown over a wall,
   *  committed on drag-end); a plain click falls through to the click-click
   *  flow via `pendingRunStart` — exactly like the opening tool. */
  private runDrawDrag: WallEdgeHit | null = null;
  /** Drag-to-draw start for a free run (`drawLikeWall` types — beams),
   *  captured on mousedown anywhere and committed on drag-end. */
  private freeRunDrag: THREE.Vector3 | null = null;

  /** Body drags, opening slide and run/beam resize handles of the SELECTED
   *  object — shared with the Design tab's selection tool. */
  private selectedDrag: SelectedObjectDragController;
  private boundRefreshHandles = (): void => this.refreshResizeHandles();

  /** A `R` keydown waiting to learn whether it's a tap (apply 45° on keyup)
   *  or the start of a hold (repeat arrives → smooth rotation, no step). */
  private pendingRotateTap: 1 | -1 | null = null;

  /** Active `R`-held smooth rotation. `before` holds each selected object's
   *  placement at hold start so the whole sweep undoes as one step (null while
   *  rotating the armed ghost, which has no undo). */
  private rotateHold: { direction: 1 | -1; lastTime: number; before: Map<string, { type: RegisteredSketchObjectType; before: PlacementValues }> | null } | null =
    null;
  private boundRotateHoldTick = (): void => this.rotateHoldTick();

  // Transform gizmo on the SELECTED object: the shared Blender-style widget
  // driving the object's PROPERTIES through SketchObjectGizmoBinding —
  // move arrows (anchor / wall placement), rotation ring (FREE objects),
  // and scale handles mapped onto the width/depth size parameters. The
  // interaction wrapper owns the choreography (arming, orbit block, undo
  // push, trailing-click swallow); rotation drags snap to 5° notches while
  // Shift is held (the same notch the creation-scroll uses).
  private gizmoBinding: SketchObjectGizmoBinding;
  private gizmoInteraction: TransformGizmoInteraction;

  private contextMenu: ContextMenu | null = null;

  /** Rubber-band box select over sketch objects. Armed on a mousedown that
   *  hits nothing (idle, no handle, no object) and started once the drag
   *  threshold confirms it's a drag, mirroring the wall editor. */
  private boxSelect: BoxSelectController | null = null;
  private boxSelectArmed = false;
  /** Whether onDragStart blocked the orbit controls for an object-interaction
   *  drag (move/resize/slide/draw) — released in onDragEnd / onDisable. */
  private dragOrbitBlocked = false;

  private unsubscribeLibrary: () => void;

  constructor(viewer: Viewer, persona: IPersona, libraryKind: SketchObjectLibraryKind = 'objects') {
    super(viewer, persona);
    this.libraryKind = libraryKind;
    this.name = libraryKind === 'dimensions' ? 'dimension' : 'sketch_object';
    this.unsubscribeLibrary = onSketchObjectTypesChanged(() => this.onLibraryChanged());
    this.hitTester = new PixelHitTester(viewer);
    // The binding renders its translation smart guides through the tool's
    // existing guide overlay (same look as body-drag alignment).
    this.gizmoBinding = new SketchObjectGizmoBinding(viewer, {
      onGuides: (guides, z) => this.showGuides(guides, z),
    });
    this.gizmoInteraction = new TransformGizmoInteraction({
      viewer,
      undoRedo: () => this.undoRedo ?? null,
      resolveBinding: () => {
        // The gizmo rides the single selected operable object — INCLUDING
        // the one just placed while the library item stays armed, so
        // placing and adjusting is one flow. Hidden only while a
        // multi-step placement is mid-flight (the rubber band owns the
        // interaction then).
        if (this.hasPendingPlacement()) return null;
        const selected = this.selectedOperableObjects();
        const object = selected.length === 1 && selected[0].getType() ? selected[0] : null;
        this.gizmoBinding.setObject(object);
        return object ? this.gizmoBinding : null;
      },
    });
    this.initUndoRedo();
    this.flipInteraction = new OpeningFlipGizmoInteraction({
      viewer,
      hitTester: this.hitTester,
      undoRedo: () => this.undoRedo ?? null,
      getWallSketch: () => this.getWallSketch(),
      manageSelectionSuppression: true,
    });
    this.selectedDrag = new SelectedObjectDragController({
      viewer,
      hitTester: this.hitTester,
      getWallSketch: () => this.getWallSketch(),
      getFloorZ: () => this.getFloorZ(),
      undoRedo: () => this.undoRedo ?? null,
      ensureOverlay: () => this.ensureOverlay(),
      onGuides: (guides, z) => (guides.length ? this.showGuides(guides, z) : this.hideGuides()),
    });
  }

  getViewConfig(): IViewConfig {
    return {
      views: [ViewModes.XRAY_2D, ViewModes.PLAN_2D_SKELETON, ViewModes.PERSPECTIVE_3D_RGB],
      defaultViewId: ViewModes.XRAY_2D.id,
    };
  }

  // ─── Library (tool settings menu) ─────────────────────────────────────

  /** The settings menu IS this tool's primary UI — open it on activation. */
  shouldAutoOpenSettings(): boolean {
    return true;
  }

  /** Library item value for an armed opening kind (and organisation door / window). */
  private static openingItemValue(kind: OpeningKind, customType: CustomOpeningTypeId | null = null): string {
    const base = kind === OpeningKind.DOOR ? 'opening:door' : 'opening:window';
    return customType ? `${base}:${customType}` : base;
  }

  /** Library items of the organisation's doors / windows (after the built-in door and window). */
  private static customOpeningItems(kind: OpeningKind) {
    return offeredCustomOpeningTypes(kind === OpeningKind.DOOR ? 'DOOR' : 'WINDOW').map((type) => ({
      value: SketchObjectTool.openingItemValue(kind, type.id),
      label: type.label,
      thumbnailSegments: buildOpeningLibraryThumbnail(kind, kind === OpeningKind.DOOR ? { doorType: type.id } : { windowType: type.id }),
      badge: t('Custom'),
    }));
  }

  getSettingsSchema(): ToolSettingsSchema {
    if (this.libraryKind === 'dimensions') return this.getDimensionSettingsSchema();
    // Doors & windows lead the library. They are NOT registry types — placing
    // one creates an OPENING_SKETCH on the wall sketch, so registering them
    // as SKETCH_OBJECT types would leak a fake type id into every registry
    // consumer (exports, left panel, per-type params).
    const categories = [
      {
        label: t('Doors & windows'),
        items: [
          { value: SketchObjectTool.openingItemValue(OpeningKind.DOOR), label: t('Door'), thumbnailSegments: buildOpeningLibraryThumbnail(OpeningKind.DOOR) },
          { value: SketchObjectTool.openingItemValue(OpeningKind.WINDOW), label: t('Window'), thumbnailSegments: buildOpeningLibraryThumbnail(OpeningKind.WINDOW) },
          ...SketchObjectTool.customOpeningItems(OpeningKind.DOOR),
          ...SketchObjectTool.customOpeningItems(OpeningKind.WINDOW),
        ],
      },
      // The library as the organisation shaped it (hidden types out, its own
      // categories and order); organisation objects carry a "Custom" badge.
      ...getSketchObjectLibrary().map((section) => ({
        label: section.label,
        items: section.types.map((type) => ({
          value: type.id,
          label: type.label,
          thumbnailSegments: type.buildDefaultGlyph(thumbnailFrame(type)),
          ...(type.custom ? { badge: t('Custom'), keywords: type.custom.tags } : {}),
        })),
      })),
    ];
    const host = getSketchObjectLibraryHost();
    const schema: ToolSettingsSchema = {
      library: {
        type: 'library',
        label: t('Library'),
        description: 'Pick an object type, then click in the layout to place it',
        defaultValue: this.armedOpening ? SketchObjectTool.openingItemValue(this.armedOpening.kind, this.armedOpening.customType) : (this.armedTypeId ?? ''),
        searchable: true,
        categories,
        recentStorageKey: 'pointorama.sketchObjectLibrary.recent',
        ...(host?.canManage ? { footerAction: { label: t('Manage library'), onClick: host.openManager } } : {}),
      },
    };
    // While a door/window is armed, surface its per-kind persisted placement
    // settings (same set + persistence as the OpeningSketchDrawTool).
    if (this.armedOpening) {
      schema.openingFixedLength = {
        type: 'category',
        label: t('Fixed length'),
        description: 'Use a fixed length instead of free-drawing',
        defaultValue: this.openingSettings.fixedLength,
      };
      schema.openingLength = {
        type: 'number',
        label: t('Width'),
        description: 'Opening width along the wall',
        defaultValue: this.openingSettings.length,
        min: 0.1,
        max: 10,
        step: 0.05,
        unit: 'm',
        group: 'openingFixedLength',
      };
      schema.openingLowerBound = {
        type: 'number',
        label: t('Ground offset'),
        description: 'Sill height above the floor',
        defaultValue: this.openingSettings.lowerBound,
        min: 0,
        max: 10,
        step: 0.05,
        unit: 'm',
      };
      schema.openingUpperBound = {
        type: 'number',
        label: t('Height'),
        description: 'Head height above the floor',
        defaultValue: this.openingSettings.upperBound,
        min: 0,
        max: 10,
        step: 0.05,
        unit: 'm',
      };
    }
    return schema;
  }

  /** The dimension tool's menu: the dimensions only (built-in, then the organisation's). */
  private getDimensionSettingsSchema(): ToolSettingsSchema {
    const host = getSketchObjectLibraryHost();
    return {
      library: {
        type: 'library',
        label: t('Dimensions'),
        description: 'Pick a dimension, then click two points to measure',
        defaultValue: this.armedTypeId ?? '',
        searchable: true,
        categories: getSketchObjectLibrary({ kind: 'dimensions' }).map((section) => ({
          label: section.label,
          items: section.types.map((type) => ({
            value: type.id,
            label: type.label,
            thumbnailSegments: type.buildDefaultGlyph(thumbnailFrame(type)),
            ...(type.custom ? { badge: t('Custom'), keywords: type.custom.tags } : {}),
          })),
        })),
        recentStorageKey: 'pointorama.dimensionLibrary.recent',
        ...(host?.canManage ? { footerAction: { label: t('Manage library'), onClick: host.openManager } } : {}),
      },
      ...this.nextDimensionSettings(),
    };
  }

  /**
   * The look of the NEXT dimensions, like the door / window placement
   * settings: the armed dimension's own options (ends, line, …) and its
   * length text. Every dimension placed starts with them; a placed one is
   * changed in its properties panel.
   */
  private nextDimensionSettings(): ToolSettingsSchema {
    const type = this.armedTypeId ? getSketchObjectType(this.armedTypeId) : null;
    if (!type?.dimension) return {};
    const params = { ...defaultParamValues(type.paramSchema), ...this.nextDimensionParams.get(type.id) };
    const schema: ToolSettingsSchema = {};
    for (const [key, spec] of Object.entries(type.paramSchema)) {
      if (key === DIMENSION_OFFSET_PARAM) continue; // set by the third click
      const value = params[key];
      const settingKey = `${DIMENSION_PARAM_SETTING}${key}`;
      if (spec.kind === 'select') schema[settingKey] = { type: 'select', label: spec.label, defaultValue: String(value), options: spec.options.map((o) => ({ value: o.value, label: o.label })) };
      else if (spec.kind === 'boolean') schema[settingKey] = { type: 'toggle', label: spec.label, defaultValue: value === true };
      else schema[settingKey] = { type: 'number', label: spec.label, defaultValue: Number(value), min: spec.min, max: spec.max, step: spec.kind === 'int' ? 1 : 0.01 };
    }
    const text = this.nextDimensionText;
    schema.dimensionDecimals = { type: 'number', label: t('decimals'), defaultValue: text.decimals, min: 0, max: 4, step: 1 };
    schema.dimensionTextSize = { type: 'number', label: t('text size'), defaultValue: text.size, min: 0.25, max: 4, step: 0.25 };
    schema.dimensionShowUnit = { type: 'toggle', label: t('show unit'), defaultValue: text.showUnit };
    schema.dimensionTextAlongLine = { type: 'toggle', label: t('text along line'), defaultValue: text.alongLine };
    return schema;
  }

  /** The dimension armed when the dimension tool activates: the default one, else the first listed. */
  private defaultDimensionTypeId(): string | null {
    const types = getSketchObjectLibrary({ kind: 'dimensions' }).flatMap((section) => section.types);
    return (types.find((type) => type.id === DEFAULT_DIMENSION_TYPE_ID) ?? types[0])?.id ?? null;
  }

  onSettingsChange(event: ToolSettingsChangeEvent): void {
    // The next dimensions' own options (see nextDimensionSettings).
    if (event.key.startsWith(DIMENSION_PARAM_SETTING) && this.armedTypeId) {
      const key = event.key.slice(DIMENSION_PARAM_SETTING.length);
      this.nextDimensionParams.set(this.armedTypeId, { ...this.nextDimensionParams.get(this.armedTypeId), [key]: event.value });
      if (this.lastHoverEvent) this.refreshGhost(this.lastHoverEvent);
      return;
    }
    switch (event.key) {
      case 'dimensionDecimals':
        this.nextDimensionText = { ...this.nextDimensionText, decimals: Number(event.value) };
        return;
      case 'dimensionShowUnit':
        this.nextDimensionText = { ...this.nextDimensionText, showUnit: Boolean(event.value) };
        return;
      case 'dimensionTextAlongLine':
        this.nextDimensionText = { ...this.nextDimensionText, alongLine: Boolean(event.value) };
        return;
      case 'dimensionTextSize':
        this.nextDimensionText = { ...this.nextDimensionText, size: Number(event.value) };
        return;
      case 'library': {
        const typeId = String(event.value);
        if (typeId) this.armType(typeId);
        else this.disarm();
        return;
      }
      case 'openingFixedLength':
        this.openingSettings.fixedLength = Boolean(event.value);
        this.persistOpeningSetting(getOpeningSketchFixedLengthProperty, this.openingSettings.fixedLength);
        return;
      case 'openingLength':
        this.openingSettings.length = Number(event.value);
        this.persistOpeningSetting(getOpeningSketchDefaultLengthProperty, this.openingSettings.length);
        return;
      case 'openingLowerBound':
        this.openingSettings.lowerBound = Number(event.value);
        this.persistOpeningSetting(getUserOpeningLowerBoundProperty, this.openingSettings.lowerBound);
        return;
      case 'openingUpperBound':
        this.openingSettings.upperBound = Number(event.value);
        this.persistOpeningSetting(getUserOpeningUpperBoundProperty, this.openingSettings.upperBound);
        return;
    }
  }

  /** Persist one opening setting for the armed kind (per-user, per-kind). */
  private persistOpeningSetting(getter: (kind: string) => { value: unknown }, value: boolean | number): void {
    try {
      getter(this.openingSettings.kind).value = value;
    } catch {
      /* property service not ready — skip persistence */
    }
  }

  /** Arm a library item for placement — an object type or (with the
   *  `opening:` prefix) a door/window OPENING_SKETCH. */
  armType(typeId: string): void {
    if (typeId.startsWith('opening:')) {
      // `opening:<door|window>[:<organisation type id>]`
      const [, kindPart, custom] = typeId.split(':');
      const kind = kindPart === 'window' ? OpeningKind.WINDOW : OpeningKind.DOOR;
      const customType = custom ? getCustomOpeningType(custom) : null;
      this.armedTypeId = null;
      this.armedOpening = { kind, customType: customType?.id ?? null };
      this.openingSettings = loadOpeningSettingsForKind(kind, this.openingSettings);
      this.rotation = 0;
      this.clearPendingPlacement();
      this.hideGhost();
      this.hideGuides();
      this.viewer.renderer.domElement.style.cursor = 'crosshair';
      // Rebuild so the per-kind opening settings block (dis)appears.
      this.getSettingsPanel()?.rebuild(this.getSettingsSchema());
      return;
    }
    if (!getSketchObjectType(typeId)) return;
    const hadOpening = this.armedOpening !== null;
    this.armedTypeId = typeId;
    this.armedOpening = null;
    this.rotation = 0;
    this.clearPendingPlacement();
    this.viewer.renderer.domElement.style.cursor = 'crosshair';
    // Dimensions: the menu shows the picked dimension's own options.
    if (hadOpening || this.libraryKind === 'dimensions') this.getSettingsPanel()?.rebuild(this.getSettingsSchema());
    else this.getSettingsPanel()?.setValue('library', typeId);
  }

  disarm(): void {
    const hadOpening = this.armedOpening !== null;
    this.armedTypeId = null;
    this.armedOpening = null;
    this.clearPendingPlacement();
    this.runDrawDrag = null;
    this.freeRunDrag = null;
    this.hideGhost();
    this.hideMarker();
    this.hideSpan();
    this.hideGuides();
    this.viewer.renderer.domElement.style.cursor = '';
    if (hadOpening) this.getSettingsPanel()?.rebuild(this.getSettingsSchema());
    else this.getSettingsPanel()?.setValue('library', '');
  }

  /** Whether a multi-step placement is mid-flight (two-click or drag-draw). */
  private hasPendingPlacement(): boolean {
    return !!(this.pendingRunStart || this.pendingFreeRunStart || this.pendingDimension || this.pendingOpeningStart || this.runDrawDrag || this.freeRunDrag || this.openingDrawDrag);
  }

  /** Clear every pending two-click / drag-draw placement start. */
  private clearPendingPlacement(): void {
    this.pendingRunStart = null;
    this.pendingFreeRunStart = null;
    this.pendingDimension = null;
    this.faceDimensionPress = false;
    this.freeRunStartDir = null;
    this.pendingOpeningStart = null;
    this.openingDrawDrag = null;
  }

  getArmedTypeId(): string | null {
    return this.armedTypeId;
  }

  // ─── Lifecycle ────────────────────────────────────────────────────────

  protected onEnable(): void {
    super.onEnable();
    this.ensureOverlay();
    // The transform gizmo renders through the shared gizmo overlay pass and
    // refreshes itself per frame (interaction wrapper).
    this.gizmoInteraction.attach();
    this.viewer.renderer.domElement.addEventListener('mousemove', this.handleHoverMove);
    // Per-frame refresh so the selected run's resize handles appear immediately
    // on select (no mouse move needed) and follow the camera.
    this.viewer.addEventListener('update', this.boundRefreshHandles);
    // Auto-select freshly-placed objects once their scene object materializes
    // (creation runs through React, so the object appears asynchronously).
    this.viewer.scene.addEventListener('sketch_object_added', this.handleSketchObjectAdded);
    this.flipInteraction.attach();
    // The dimension tool is ready to measure right away.
    if (this.libraryKind === 'dimensions' && !this.armed) {
      const typeId = this.defaultDimensionTypeId();
      if (typeId) this.armType(typeId);
    }
  }

  protected onDisable(): void {
    this.gizmoInteraction.detach();
    this.flipInteraction.detach();
    this.pendingRotateTap = null;
    this.endRotateHold();
    this.pendingAutoSelectId = null;
    this.viewer.scene.removeEventListener('sketch_object_added', this.handleSketchObjectAdded);
    this.viewer.renderer.domElement.removeEventListener('mousemove', this.handleHoverMove);
    this.viewer.removeEventListener('update', this.boundRefreshHandles);
    // Safety: never leave selection suppressed if the tool is torn down mid-resize.
    this.viewer.inputHandler.selectionDisabled = false;
    this.viewer.renderer.domElement.style.cursor = '';
    this.armedTypeId = null;
    this.armedOpening = null;
    this.clearPendingPlacement();
    this.runDrawDrag = null;
    this.freeRunDrag = null;
    if (this.boxSelect?.isActive()) this.viewer.inputHandler.unblockOrbitControls();
    this.boxSelect?.dispose();
    this.boxSelect = null;
    this.boxSelectArmed = false;
    if (this.dragOrbitBlocked) {
      this.dragOrbitBlocked = false;
      this.viewer.inputHandler.unblockOrbitControls();
    }
    this.selectedDrag.dispose();
    this.gizmoBinding.setObject(null);
    this.contextMenu?.dispose();
    this.contextMenu = null;
    this.clearImaginedWallEdge();
    this.disposeOverlay();
    super.onDisable();
  }

  dispose(): void {
    this.unsubscribeLibrary();
    this.gizmoInteraction.dispose();
    super.dispose();
  }

  /**
   * Custom types arrive (and change) after the tool exists: refresh an open
   * library and drop an armed type that disappeared or was hidden.
   */
  private onLibraryChanged(): void {
    if (this.armedTypeId && !getSketchObjectLibrary({ kind: this.libraryKind }).some((section) => section.types.some((type) => type.id === this.armedTypeId))) this.disarm();
    const armedCustom = this.armedOpening?.customType;
    if (armedCustom && !getCustomOpeningType(armedCustom)) this.disarm();
    this.rebuildSettingsPanelIfCreated();
  }

  // ─── Placement context ────────────────────────────────────────────────

  private getWallSketch(): PointoramaWallSketch | null {
    if (!this.currentFloorGroupIdentifier) return null;
    return this.viewer.scene.wallSketches.find((s) => s.groupIdentifier === this.currentFloorGroupIdentifier) || null;
  }

  // ─── Room/outline-edge fallback (no sketch wall under the cursor) ─────
  //
  // Seamless-UX contract: a wall gap WITHOUT a sketch edge must interact
  // exactly like one WITH an edge. While a wall-needing placement is armed
  // and the cursor sits over a room/outline wall gap with no sketch edge in
  // reach, a TRANSIENT (unpersisted) loose edge is inserted into the sketch
  // data — the normal snap-driven previews (marker, spans, ghost) then run
  // against it unchanged. Placement persists the edge; hovering away or
  // disarming removes it as if it was never there.

  /** The transient imagined edge, keyed by the pick's centerline identity so
   *  hovering along the same wall gap reuses one edge. */
  private imaginedWallEdge: { edgeId: string; key: string } | null = null;

  /**
   * Whether the armed placement can interact with walls: openings and
   * WALL-constrained types require one; FREE types (bath, furniture)
   * wall-ALIGN and collide when near one, so they want the imagined edge
   * too. Effectively every armed placement.
   */
  private armedNeedsWall(): boolean {
    // Dimensions measure what exists: they never imagine a wall.
    return this.armedOpening !== null || (this.armedTypeId !== null && this.libraryKind === 'objects');
  }

  /**
   * World-meter reach for the inside-room imagined-edge pick — matches the
   * "dropped near a wall" feel of the FREE alignment (footprint contact plus
   * a little approach margin).
   */
  private static readonly IMAGINED_EDGE_ROOM_REACH_M = 1.5;

  /** Room/outline pick under the cursor (see RoomEdgeFallback), or null.
   *  Inside-room picking is enabled so FREE alignment (bath against a wall)
   *  gets its imagined edge while the cursor is in the room next to it. */
  private computeRoomPickFallback(event: MouseEvent): RoomEdgePick | null {
    const floorId = this.currentFloorGroupIdentifier;
    if (!floorId) return null;
    // Imagined walls are a MEASURED-source affordance: in the standardised
    // (wall-sketch) mode the sketch is authoritative — missing walls are
    // drawn in the wall editor, never imagined from rooms.
    if (!(this.viewer.floorplanRepresentation?.isOriginalActive(floorId) ?? false)) return null;
    const rooms = this.viewer.scene.rooms.filter((r) => r.groupIdentifier === floorId);
    const outlines = this.viewer.scene.outlines.filter((o) => (o as PointoramaOutline).groupIdentifier === floorId) as PointoramaOutline[];
    if (rooms.length === 0 && outlines.length === 0) return null;
    const floorZ = this.getFloorZ();
    const cursorWorld = projectMouseToFloor(event, this.viewer.renderer.domElement, this.viewer.scene.getActiveCamera(), floorZ);
    if (!cursorWorld) return null;
    const edges = [...collectRoomEdges(rooms), ...collectOutlineEdges(outlines)];
    if (edges.length === 0) return null;
    const outlineRings = outlines.map((o) => o.getOuterRing());
    return pickRoomEdgeAtPixel(edges, rooms, this.hitTester.mousePx(event), cursorWorld, this.hitTester, floorZ, outlineRings, {
      allowInsideRoom: true,
      maxCursorEdgeDistanceM: SketchObjectTool.IMAGINED_EDGE_ROOM_REACH_M,
    });
  }

  /** Stable identity for a pick's wall gap (cm-resolution centerline). */
  private static roomPickIdentityKey(pick: RoomEdgePick): string {
    const r = (v: number) => Math.round(v * 100);
    return `${r(pick.centerlineStart.x)},${r(pick.centerlineStart.y)}:${r(pick.centerlineEnd.x)},${r(pick.centerlineEnd.y)}`;
  }

  /**
   * Keep the transient imagined edge in sync with the cursor. MUST run
   * before the preview refreshers each mousemove so they snap against it.
   */
  private syncImaginedWallEdge(event: MouseEvent): void {
    if (!this.armedNeedsWall()) {
      this.clearImaginedWallEdge();
      return;
    }
    const sketch = this.getWallSketch();
    if (!sketch) return;

    const hit = this.snapOpeningWall(event) ?? this.snapWall(event);
    if (hit) {
      // Over a real wall — or over the imagined one, which must stay put so
      // the ongoing preview/draw keeps its target.
      if (this.imaginedWallEdge && hit.edgeId !== this.imaginedWallEdge.edgeId) {
        this.clearImaginedWallEdge();
      }
      return;
    }

    const pick = this.computeRoomPickFallback(event);
    if (!pick) {
      this.clearImaginedWallEdge();
      return;
    }
    const key = SketchObjectTool.roomPickIdentityKey(pick);
    if (this.imaginedWallEdge?.key === key) return;
    this.clearImaginedWallEdge();
    const edgeId = materializeWallEdgeFromRoomPick(this.viewer, sketch, pick, { persist: false });
    this.imaginedWallEdge = { edgeId, key };
  }

  /** Remove the transient edge (data-only — it was never persisted). */
  private clearImaginedWallEdge(): void {
    if (!this.imaginedWallEdge) return;
    const sketch = this.getWallSketch();
    if (sketch) removeLooseWallEdge(sketch, this.imaginedWallEdge.edgeId);
    this.imaginedWallEdge = null;
  }

  /**
   * Placement interaction: promote the imagined edge to a real, persisted
   * one when the interaction targets it — from here on it IS a normal wall.
   * Creates the transient first when the interaction arrives without a
   * preceding hover (touch / synthesized events).
   */
  private commitImaginedWallEdge(event: MouseEvent): void {
    this.syncImaginedWallEdge(event);
    if (!this.imaginedWallEdge) return;
    const sketch = this.getWallSketch();
    if (!sketch) {
      this.imaginedWallEdge = null;
      return;
    }
    // "Used the imagined wall" = the cursor snaps onto it (openings, wall
    // objects, runs) OR the FREE ghost is currently wall-ALIGNED to it (bath
    // flush against the wall — the cursor sits inside the room then, so the
    // pixel snap can't see it; the alignment's sticky edge can).
    const hit = this.snapOpeningWall(event) ?? this.snapWall(event);
    const targeted = (hit && hit.edgeId === this.imaginedWallEdge.edgeId) || getStickyAlignedEdgeId() === this.imaginedWallEdge.edgeId;
    if (targeted) {
      persistWallSketch(this.viewer, sketch);
      this.imaginedWallEdge = null;
    }
  }

  private getFloorZ(): number {
    return this.getWallSketch()?.getFloorHeight() ?? this.currentFloorFloorPlane?.height ?? 0;
  }

  /**
   * Selected sketch objects the tool may actually operate on. The selection
   * can retain objects on OTHER floors (the per-floor filter only hides them
   * frame-locally, `.visible` reads true between frames), and tool actions —
   * rotate, delete, handles — must never touch those.
   */
  private selectedOperableObjects(): PointoramaSketchObject[] {
    return this.viewer.scene.sketchObjects.filter((o) => o.selected && o.visible && !isAnnotationPickBlocked(this.viewer, o, 'SKETCH_OBJECT'));
  }

  private placementContext(event: MouseEvent): PlacementContext {
    return {
      viewer: this.viewer,
      hitTester: this.hitTester,
      event,
      wallSketch: this.getWallSketch(),
      floorZ: this.getFloorZ(),
    };
  }

  // ─── Hover / ghost ────────────────────────────────────────────────────

  private handleHoverMove = (event: MouseEvent): void => {
    if (!this.enabled) return;
    // Mid box select the rubber-band owns the interaction — no hover feedback.
    if (this.boxSelect?.isActive()) return;
    this.lastHoverEvent = event;

    // A flip gizmo under the cursor wins the hover (click flips it).
    if (this.flipInteraction.isOverHandle(event)) {
      this.viewer.renderer.domElement.style.cursor = 'pointer';
      return;
    }

    // The transform gizmo wins next — ALSO while a library item is armed:
    // it rides the just-placed object so placing and adjusting is one flow.
    // Hovering it suppresses the placement ghost (a click there grabs the
    // handle, it doesn't place).
    if (this.gizmoInteraction.handleMouseMove(event)) {
      this.viewer.renderer.domElement.style.cursor = 'grab';
      this.hideGhost();
      this.hideMarker();
      this.hideSpan();
      this.hideGuides();
      return;
    }

    // Same for the run-resize end handles of a just-placed run.
    if (!this.hasPendingPlacement() && this.pickResizeHandleAtPixel(this.hitTester.mousePx(event))) {
      this.viewer.renderer.domElement.style.cursor = 'ew-resize';
      this.hideGhost();
      this.hideMarker();
      this.hideSpan();
      this.hideGuides();
      return;
    }

    // Armed, over something already placed: it can be grabbed / selected — no placement preview.
    if (this.placedUnderCursor(event)) {
      this.viewer.renderer.domElement.style.cursor = 'move';
      this.clearImaginedWallEdge();
      this.hideGhost();
      this.hideMarker();
      this.hideSpan();
      this.hideGuides();
      return;
    }

    if (this.armedOpening) {
      // Imagined-edge sync FIRST: the preview refresh below then snaps
      // against the transient edge exactly as it would against a real wall.
      this.syncImaginedWallEdge(event);
      this.refreshOpeningPreview(event);
      return;
    }

    if (this.armedTypeId) {
      // Restore the placement cursor after a gizmo hover set 'grab'.
      this.viewer.renderer.domElement.style.cursor = 'crosshair';
      this.syncImaginedWallEdge(event);
      this.refreshGhost(event);
      return;
    }

    // Idle: move cursor over an existing object or opening sketch (click
    // selects, drag moves/slides). Gizmo + resize handles were checked above.
    this.clearImaginedWallEdge();
    const mousePx = this.hitTester.mousePx(event);
    const hovered = pickSketchObjectAtPixel(this.viewer, mousePx, this.hitTester, GLYPH_PICK_RADIUS_PX);
    if (hovered) {
      this.viewer.renderer.domElement.style.cursor = 'move';
      return;
    }
    const skeleton = this.getWallSketch();
    const hoveredOpening = skeleton ? pickOpeningSketchAtPixel(this.viewer, skeleton, mousePx, this.hitTester) : null;
    this.viewer.renderer.domElement.style.cursor = hoveredOpening || this.pickBeamByFootprint(event) ? 'move' : '';
  };

  // ─── Opening placement (armed door/window) ───────────────────────────

  /** Cursor snapped onto the wall sketch centerline with the opening tool's radii. */
  private snapOpeningWall(event: MouseEvent): OpeningEdgeHit | null {
    const skeleton = this.getWallSketch();
    if (!skeleton) return null;
    return snapToWallEdge(event, skeleton, this.hitTester, this.viewer.scene.getActiveCamera(), this.viewer.renderer.domElement, {
      edgeRadiusPx: OPENING_EDGE_RADIUS_PX,
      nodeSnapRadiusPx: OPENING_NODE_SNAP_RADIUS_PX,
    });
  }

  /** Hover preview while a door/window is armed: snap marker on the wall
   *  centerline plus the span (fixed-length centered on the cursor, or the
   *  rubber-band from a pending free-draw start). Mirrors OpeningSketchDrawTool. */
  private refreshOpeningPreview(event: MouseEvent): void {
    const skeleton = this.getWallSketch();
    if (!skeleton) {
      this.hideMarker();
      this.hideSpan();
      return;
    }
    const hit = this.snapOpeningWall(event);
    this.viewer.renderer.domElement.style.cursor = hit ? 'pointer' : 'crosshair';
    if (!hit) {
      this.hideMarker();
      this.hideSpan();
      return;
    }
    this.showMarker(hit.point);

    if (this.pendingOpeningStart && this.pendingOpeningStart.edgeId === hit.edgeId && !this.openingSettings.fixedLength) {
      // Both ends held inside the corner-clear span, like the commit.
      const span = openingClearSpanOnEdge(skeleton, this.pendingOpeningStart.edgeId);
      const s0 = Math.max(span.start, Math.min(span.end, this.pendingOpeningStart.offset));
      const s1 = Math.max(span.start, Math.min(span.end, hit.offset));
      const pts = openingSpanPolyline(skeleton, this.pendingOpeningStart.edgeId, s0, s1);
      if (pts) this.showSpanPolyline(pts);
    } else if (this.openingSettings.fixedLength) {
      const lo = clampOpeningOffsetToClearSpan(skeleton, hit.edgeId, this.openingSettings.length, hit.offset - this.openingSettings.length / 2);
      const pts = openingSpanPolyline(skeleton, hit.edgeId, lo, lo + this.openingSettings.length);
      if (pts) this.showSpanPolyline(pts);
    } else {
      this.hideSpan();
    }
  }

  /** Commit an armed door/window span as an OPENING_SKETCH + undo snapshot. */
  private createOpeningFromSpan(skeleton: PointoramaWallSketch, edgeId: string, offset: number, length: number): void {
    const request = buildOpeningSketchRequest(skeleton, {
      edgeId,
      offset,
      length,
      kind: this.openingSettings.kind,
      lowerBound: this.openingSettings.lowerBound,
      upperBound: this.openingSettings.upperBound,
      // An organisation door / window armed from the library: the new opening uses it.
      ...(this.armedOpening?.customType ? (this.openingSettings.kind === OpeningKind.WINDOW ? { windowType: this.armedOpening.customType } : { doorType: this.armedOpening.customType }) : {}),
    });
    if (!request) return;
    this.viewer.scene.dispatchEvent({ type: 'request_opening_sketch', value: request });
    this.undoRedo?.pushState(createOpeningToggleSnapshot(this.viewer, request, true));
  }

  /** Selected opening sketches on the current floor the tool may operate on. */
  private selectedOperableOpenings(): PointoramaOpeningSketch[] {
    const floorId = this.currentFloorGroupIdentifier;
    if (!floorId) return [];
    return this.viewer.scene.openingSketches.filter((o) => o.selected && o.groupIdentifier === floorId && !isAnnotationPickBlocked(this.viewer, o, 'OPENING_SKETCH'));
  }

  /** Delete an opening sketch with a re-creatable undo snapshot. */
  private deleteOpeningSketch(opening: PointoramaOpeningSketch): void {
    const request = opening.groupIdentifier ? captureOpeningSketchRequest(opening.identifier, opening.groupIdentifier) : null;
    this.viewer.scene.dispatchEvent({ type: 'measurement_delete', value: { measure: opening } });
    if (request) this.undoRedo?.pushState(createOpeningToggleSnapshot(this.viewer, request, false));
  }

  /** Per-frame footprint highlight over the SELECTED openings (plus the one
   *  being slid, which onDrag refreshes itself). Hidden while armed. */
  private refreshOpeningHighlight(): void {
    if (this.armed) {
      this.openingHighlight.hide();
      return;
    }
    if (this.selectedDrag.openingSlideMoved()) return; // slide owns the highlight mid-drag
    const skeleton = this.getWallSketch();
    if (!skeleton) {
      this.openingHighlight.hide();
      return;
    }
    const polylines: THREE.Vector3[][] = [];
    for (const opening of this.selectedOperableOpenings()) {
      const footprint = openingFootprint(skeleton, opening.identifier);
      if (footprint) polylines.push(footprint.pts);
    }
    if (polylines.length === 0) {
      this.openingHighlight.hide();
      return;
    }
    this.openingHighlight.show(polylines);
  }

  /** LINE-anchored WALL types are drawn as a run along a wall (two clicks). */
  private isWallRunType(type: RegisteredSketchObjectType): boolean {
    return type.anchor === SketchObjectAnchorKind.LINE && type.constraint.kind === 'WALL';
  }

  /**
   * Every LINE-anchored type supports the run-draw UX when the cursor is on a
   * wall. WALL-constrained ones (cabinets, radiators) draw ONLY that way;
   * FREE ones (stairs) are hybrid — span-draw on a wall, stampable ghost
   * elsewhere.
   */
  private isRunDrawType(type: RegisteredSketchObjectType): boolean {
    return type.anchor === SketchObjectAnchorKind.LINE;
  }

  /** Span placement between two hits, keyed on the type's constraint: WALL
   *  attaches parametrically; FREE persists an aligned world anchor. */
  private resolveRunSpan(event: MouseEvent, type: RegisteredSketchObjectType, start: WallEdgeHit, end: WallEdgeHit): ResolvedPlacement | null {
    const ctx = this.placementContext(event);
    return type.constraint.kind === 'WALL' ? resolveWallSpan(ctx, type, start, end) : resolveFreeWallSpan(ctx, type, start, end);
  }

  private snapWall(event: MouseEvent): WallEdgeHit | null {
    const skeleton = this.getWallSketch();
    if (!skeleton) return null;
    return snapToWallEdge(event, skeleton, this.hitTester, this.viewer.scene.getActiveCamera(), this.viewer.renderer.domElement, SKETCH_OBJECT_WALL_SNAP);
  }

  private static snapOverridden(event: MouseEvent): boolean {
    return snapOverridden(event);
  }

  /** Endpoint of a free run (`drawLikeWall`): snapped onto a nearby wall
   *  centerline (smart connect), else the raw floor point under the cursor.
   *  Null when the cursor doesn't project onto the floor plane. */
  private snapFreeRunPoint(event: MouseEvent): THREE.Vector3 | null {
    return this.snapFreeRunPointInfo(event)?.point ?? null;
  }

  /** Dimensions snap to the wall skin (corners, faces), other free runs to wall centerlines. */
  private snapFreeRunPointInfo(event: MouseEvent): FreeRunPointInfo | null {
    const snap = this.armedTypeId && getSketchObjectType(this.armedTypeId)?.dimension ? snapDimensionPointInfo : snapFreeRunPointInfo;
    return snap(this.viewer, this.hitTester, this.getWallSketch(), this.getFloorZ(), event);
  }

  /**
   * Resolve a beam's END endpoint against `start`. The 90° direction snap
   * references the WALLS at the run's endpoints — the wall the run STARTED
   * on first, else the wall under the cursor; a run touching no wall at
   * either end doesn't angle-snap at all:
   *  - free endpoint → 90° direction snap against the reference wall;
   *  - wall endpoint, direction off any step → plain wall snap;
   *  - wall endpoint AND direction near a step → DOUBLE SNAP: the end lands
   *    ON the wall at its intersection with the snapped direction ray, so
   *    the beam is square AND connected.
   * Shift/Ctrl suppress every snap — the end is the raw cursor point.
   */
  /**
   * Both ends of a free run against the cursor. Beams resolve their end
   * ({@link resolveBeamEnd}); a dimension snaps to 90° steps of the wall at
   * its start (see squareDimensionRun), possibly sliding a start that sits on
   * a face. Shift / Ctrl: the raw cursor.
   */
  private resolveRunEnds(type: RegisteredSketchObjectType, event: MouseEvent, start: THREE.Vector3): { start: THREE.Vector3; end: THREE.Vector3 } | null {
    if (!type.dimension) {
      const end = this.resolveBeamEnd(event, start);
      return end ? { start, end } : null;
    }
    const info = this.snapFreeRunPointInfo(event);
    if (!info) return null;
    if (SketchObjectTool.snapOverridden(event)) return { start, end: info.point };
    return squareDimensionRun(start, skinSidesAt(this.getWallSketch(), start), info);
  }

  private resolveBeamEnd(event: MouseEvent, start: THREE.Vector3): THREE.Vector3 | null {
    const info = this.snapFreeRunPointInfo(event);
    if (!info) return null;
    if (SketchObjectTool.snapOverridden(event)) return info.point;
    const referenceDir = this.freeRunStartDir ?? info.edgeDir;
    const angleSnapped = referenceDir ? snapSegmentTo90(start, info.point, referenceDir) : null;
    if (!info.onWall) return angleSnapped ?? info.point;
    if (angleSnapped && info.edgeId) {
      return this.intersectRayWithEdge(start, angleSnapped, info.edgeId, info.point) ?? info.point;
    }
    return info.point;
  }

  /**
   * The pending dimension (its two measured points) and the offset the cursor
   * pulls its line to: the signed distance of the cursor (snapped to wall
   * corners / faces, so a line can line up with a wall) from the measured line.
   */
  private resolveDimensionOffset(type: RegisteredSketchObjectType, event: MouseEvent): { placement: ResolvedPlacement; offset: number; cursor: THREE.Vector3 } | null {
    if (!this.pendingDimension) return null;
    const placement = resolveFreeRun(type, this.pendingDimension.start, this.pendingDimension.end, this.getFloorZ());
    const cursor = this.snapFreeRunPoint(event);
    if (!placement || !cursor) return null;
    const { center, norm } = placement.frame;
    const offset = (cursor.x - center.x) * norm.x + (cursor.y - center.y) * norm.y;
    return { placement, offset, cursor };
  }

  /** Both measured points of a dimension are set: wait for the offset click (other runs commit right away). */
  private finishFreeRun(type: RegisteredSketchObjectType, start: THREE.Vector3, end: THREE.Vector3, placement: ResolvedPlacement): void {
    if (type.dimension) {
      this.pendingDimension = { start, end };
      return;
    }
    this.createSketchObject(type, placement);
  }

  private intersectRayWithEdge(start: THREE.Vector3, through: THREE.Vector3, edgeId: string, nearTo: THREE.Vector3): THREE.Vector3 | null {
    return intersectRayWithEdge(this.getWallSketch(), start, through, edgeId, nearTo);
  }

  /** World point at `offset` along a wall edge centerline (arc length on curved edges). */
  private edgeWorldPoint(geom: WallEdgeGeometry, offset: number, z: number): THREE.Vector3 {
    const { point } = pointAtArcLength(geom.a, geom.b, geom.bulge, offset);
    return new THREE.Vector3(point.x, point.y, z);
  }

  /** Live glyph ghost of the run being drawn (shown on top of the span line). */
  private showRunPreview(type: RegisteredSketchObjectType, event: MouseEvent, start: WallEdgeHit, end: WallEdgeHit): void {
    const placement = this.resolveRunSpan(event, type, start, end);
    if (!placement || placement.frame.width < MIN_RUN_LENGTH) {
      this.hideGhost();
      return;
    }
    this.showGhost(type.buildDefaultGlyph(placement.frame), placement.frame);
  }

  private refreshGhost(event: MouseEvent): void {
    const type = this.armedTypeId ? getSketchObjectType(this.armedTypeId) : null;
    if (!type) {
      this.hideGhost();
      this.hideMarker();
      this.hideSpan();
      this.hideGuides();
      return;
    }

    // drawLikeWall runs (beams) use the wall-editor draw UX EVERYWHERE: a
    // snap marker (wall-snapped when near one — smart connect), then a
    // rubber-band span + live glyph between the two points.
    if (type.drawLikeWall) {
      this.hideGuides();
      // Dimension, both points set: the cursor pulls the line off them.
      if (this.pendingDimension) {
        const pulled = this.resolveDimensionOffset(type, event);
        if (!pulled) {
          this.hideMarker();
          this.hideGhost();
          return;
        }
        this.showMarker(pulled.cursor);
        this.hideSpan();
        this.showGhost(type.withParams({ ...this.nextDimensionParams.get(type.id), [DIMENSION_OFFSET_PARAM]: pulled.offset }).glyph(pulled.placement.frame), pulled.placement.frame);
        return;
      }
      // Mid-draw the end resolves against the start (wall snap first, else
      // the 90° direction snap); before the first point it's the plain snap.
      const pressed = this.freeRunDrag ?? this.pendingFreeRunStart;
      // A dimension before its first point: a wall face under the cursor (no corner within reach)
      // lights up whole — a click measures it in one go.
      if (!pressed && type.dimension) {
        const face = this.snapFreeRunPointInfo(event)?.face;
        if (face) {
          this.hideMarker();
          this.hideGhost();
          this.showSpan(face.a, face.b);
          return;
        }
      }
      const ends = pressed ? this.resolveRunEnds(type, event, pressed) : null;
      const snap = pressed ? (ends?.end ?? null) : this.snapFreeRunPoint(event);
      if (!snap) {
        this.hideMarker();
        this.hideSpan();
        this.hideGhost();
        return;
      }
      this.showMarker(snap);
      if (ends) {
        this.showSpan(ends.start, snap);
        const placement = resolveFreeRun(type, ends.start, snap, this.getFloorZ());
        if (placement && placement.frame.width >= MIN_RUN_LENGTH) this.showGhost(type.withParams(this.nextDimensionParams.get(type.id) ?? {}).glyph(placement.frame), placement.frame);
        else this.hideGhost();
      } else {
        this.hideSpan();
        this.hideGhost();
      }
      return;
    }

    // Run-draw types use the opening draw tool's UX while ON a wall: a snap
    // marker on the wall and, while drawing (click-click or drag), a fat
    // rubber-band span line — the full glyph appears on commit, not as a
    // hover ghost. FREE run types are hybrid: off-wall they fall through to
    // the normal stampable ghost below.
    if (this.isRunDrawType(type)) {
      const hit = this.snapWall(event);
      if (hit) {
        // On the wall the span/marker UX takes over — no peer guides.
        this.hideGuides();
        this.showMarker(hit.point);
        // A hit on a COLINEAR continuation edge remaps into the start edge's
        // along-line coordinate, so runs draw across T-junction nodes.
        const skeleton = this.getWallSketch();
        const mapped = this.pendingRunStart && skeleton ? remapHitToEdgeLine(skeleton, this.pendingRunStart.edgeId, hit) : null;
        if (this.pendingRunStart && skeleton && mapped) {
          const geom = wallEdgeGeometry(skeleton, mapped.edgeId);
          if (geom) {
            const z = skeleton.getFloorHeight();
            this.showSpan(this.edgeWorldPoint(geom, this.pendingRunStart.offset, z), this.edgeWorldPoint(geom, mapped.offset, z));
            // Live glyph preview of the run on top of the span line, so the
            // skinning (modules/fins/treads) is visible while drawing.
            this.showRunPreview(type, event, this.pendingRunStart, mapped);
            return;
          }
        }
        this.hideGhost();
        this.hideSpan();
        return;
      }
      this.hideMarker();
      this.hideSpan();
      // Wall-only runs have nothing to preview off-wall; mid-draw the span
      // just waits for the cursor to return to the wall.
      if (type.constraint.kind === 'WALL' || this.pendingRunStart) {
        this.hideGhost();
        this.hideGuides();
        return;
      }
    } else {
      this.hideMarker();
      this.hideSpan();
    }
    const strategy = getConstraintStrategy(type.constraint.kind);
    const placement = strategy.resolveHover(this.placementContext(event), type, this.rotation);
    if (!placement) {
      this.hideGhost();
      this.hideGuides();
      return;
    }
    this.showGhost(type.buildDefaultGlyph(placement.frame), placement.frame);
    this.showGuides(placement.guides ?? [], placement.frame.z);
  }

  // ─── Resize handles (selected wall run) ──────────────────────────────

  /**
   * Show/refresh the run resize handles when exactly one RUN object (any
   * LINE-anchored type — wall runs like cabinets, free runs like stairs,
   * beams) is selected; hide them otherwise. Called per frame — positions
   * follow the object and the camera (display + drags live in the shared
   * SelectedObjectDragController).
   */
  private refreshResizeHandles(): void {
    if (!this.enabled) return;
    // Selected openings' footprint highlight rides the same per-frame tick so
    // it appears immediately on select and tracks the camera.
    this.refreshOpeningHighlight();
    const selected = this.selectedOperableObjects();
    const object = selected.length === 1 ? selected[0] : null;
    // The handles stay up while a library item is armed (a just-placed run
    // is selected then — growable from one side right away); they hide only
    // mid multi-click/drag-draw placement.
    this.selectedDrag.refreshHandles(object && !this.hasPendingPlacement() ? object : null);
  }

  /** The run handle under a pixel cursor, if any ('middle' only for beams;
   *  `{ depth }` = index into the custom depth handles of a free run). */
  private pickResizeHandleAtPixel(mousePx: THREE.Vector2): ResizeHandleEnd | null {
    return this.selectedDrag.pickHandleAtPixel(mousePx);
  }

  private cursorOffsetOnEdge(event: MouseEvent, floorZ: number, geom: WallEdgeGeometry): number | null {
    return cursorOffsetOnEdge(this.viewer, this.getWallSketch(), event, floorZ, geom);
  }



  /**
   * The beam (`drawLikeWall`) whose rectangle FOOTPRINT contains the cursor's
   * floor point. Beams interact by their whole square — centerline and
   * interior included — not just the dashed outline, whose segments are also
   * wall-clipped and may be sparse. Used as a FALLBACK after the precise
   * glyph/opening picks, so small targets inside the square stay grabbable.
   */
  private pickBeamByFootprint(event: MouseEvent): PointoramaSketchObject | null {
    const floor = projectMouseToFloor(event, this.viewer.renderer.domElement, this.viewer.scene.getActiveCamera(), this.getFloorZ());
    if (!floor) return null;
    for (const obj of this.viewer.scene.sketchObjects) {
      if (!obj.visible || isAnnotationPickBlocked(this.viewer, obj, 'SKETCH_OBJECT')) continue;
      if (!obj.getType()?.drawLikeWall) continue;
      const frame = obj.resolveFrame();
      if (!frame) continue;
      const dx = floor.x - frame.center.x;
      const dy = floor.y - frame.center.y;
      const u = dx * frame.dir.x + dy * frame.dir.y;
      const v = dx * frame.norm.x + dy * frame.norm.y;
      if (Math.abs(u) <= frame.width / 2 && Math.abs(v) <= frame.depth / 2) return obj;
    }
    return null;
  }

  private tryArmResizeHandleDrag(event: MouseEvent): boolean {
    return this.selectedDrag.armResizeHandle(event);
  }

  private armBeamBodyDrag(object: PointoramaSketchObject, event: MouseEvent): void {
    this.selectedDrag.armBeamBodyDrag(object, event);
  }





  private ensureOverlay(): THREE.Group {
    if (!this.overlay) {
      this.overlay = new THREE.Group();
      this.overlay.name = 'sketch_object_overlay';
      this.viewer.uiScene.add(this.overlay);
    }
    return this.overlay;
  }

  /** Draw the ghost glyph. Vertices localized around the frame center (UTM precision). */
  private showGhost(worldSegments: number[], frame: SketchObjectFrame): void {
    const overlay = this.ensureOverlay();
    if (!this.ghostLine) {
      const rendererSize = new THREE.Vector2();
      this.viewer.renderer.getSize(rendererSize);
      this.ghostMaterial = new LineMaterial({
        color: GHOST_COLOR,
        linewidth: GHOST_LINE_WIDTH,
        resolution: rendererSize,
        depthTest: false,
        transparent: true,
        opacity: 0.9,
      });
      // Same per-pixel inversion as the placed default-black glyph, so the
      // preview matches the committed result on both plan backgrounds.
      applyBackdropInvert(this.ghostMaterial, this.viewer.backdropCapture);
      this.ghostGeometry = new LineSegmentsGeometry();
      this.ghostLine = new LineSegments2(this.ghostGeometry, this.ghostMaterial);
      this.ghostLine.renderOrder = GHOST_RENDER_ORDER;
      this.ghostLine.frustumCulled = false;
      overlay.add(this.ghostLine);
    }

    const local = new Array<number>(worldSegments.length);
    for (let i = 0; i < worldSegments.length; i += 3) {
      local[i] = worldSegments[i] - frame.center.x;
      local[i + 1] = worldSegments[i + 1] - frame.center.y;
      local[i + 2] = worldSegments[i + 2];
    }
    this.ghostGeometry?.dispose();
    this.ghostGeometry = new LineSegmentsGeometry();
    this.ghostGeometry.setPositions(local.length > 0 ? local : [0, 0, 0, 0, 0, 0]);
    this.ghostLine.geometry = this.ghostGeometry;
    this.ghostLine.position.set(frame.center.x, frame.center.y, 0);
    this.ghostLine.computeLineDistances();
    this.ghostLine.visible = true;
  }

  private hideGhost(): void {
    if (this.ghostLine) this.ghostLine.visible = false;
  }

  private showMarker(at: THREE.Vector3): void {
    const group = this.ensureOverlay();
    if (!this.hoverMarker) {
      this.hoverMarker = new SphereMarker(this.viewer);
      this.hoverMarker.setScaleFactor(1.2);
      this.hoverMarker.setRenderOrder(GHOST_RENDER_ORDER);
      group.add(this.hoverMarker);
    }
    this.hoverMarker.setColor(gizmoContrastColor(this.viewer, GHOST_COLOR));
    this.hoverMarker.position.set(at.x, at.y, at.z + 0.03);
    this.hoverMarker.visible = true;
  }

  private hideMarker(): void {
    if (this.hoverMarker) this.hoverMarker.visible = false;
  }

  /** Draw the colored run span being created, lying ON the wall edge between a and b. */
  private showSpan(a: THREE.Vector3, b: THREE.Vector3): void {
    this.showSpanPolyline([a, b]);
  }

  private hideSpan(): void {
    if (this.spanLine) this.spanLine.visible = false;
  }

  /** Polyline variant of {@link showSpan} — needed for opening spans on
   *  CURVED walls, whose footprint is a tessellated arc rather than a single
   *  segment. Swaps the geometry whenever the polyline changes because
   *  LineGeometry buffers are sized at construction (vertex counts vary). */
  private showSpanPolyline(pts: THREE.Vector3[]): void {
    if (pts.length < 2) {
      this.hideSpan();
      return;
    }
    const group = this.ensureOverlay();
    const positions: number[] = [];
    for (const p of pts) positions.push(p.x, p.y, p.z + 0.04);
    const resolution = new THREE.Vector2(this.viewer.renderer.domElement.clientWidth || 1000, this.viewer.renderer.domElement.clientHeight || 1000);
    if (!this.spanLine) {
      this.spanGeometry = new LineGeometry();
      this.spanGeometry.setPositions(positions);
      this.spanMaterial = new LineMaterial({
        color: DRAW_LINE_COLOR.getHex(),
        linewidth: DRAW_LINE_WIDTH,
        resolution,
        depthTest: false,
        transparent: true,
      });
      this.spanLine = new Line2(this.spanGeometry, this.spanMaterial);
      this.spanLine.renderOrder = GHOST_RENDER_ORDER;
      this.spanLine.computeLineDistances();
      group.add(this.spanLine);
    } else {
      const old = this.spanGeometry;
      this.spanGeometry = new LineGeometry();
      this.spanGeometry.setPositions(positions);
      this.spanLine.geometry = this.spanGeometry;
      old?.dispose();
      this.spanLine.computeLineDistances();
      this.spanMaterial?.resolution.copy(resolution);
    }
    this.spanLine.visible = true;
  }

  /** Draw the smart-guide alignment lines (empty array hides them). Vertices
   *  localized around the first point (UTM precision, like the ghost). */
  private showGuides(guides: AlignGuide[], z: number): void {
    if (guides.length === 0) {
      this.hideGuides();
      return;
    }
    const overlay = this.ensureOverlay();
    if (!this.guideLine) {
      const rendererSize = new THREE.Vector2();
      this.viewer.renderer.getSize(rendererSize);
      this.guideMaterial = new LineMaterial({
        color: GUIDE_COLOR,
        linewidth: GUIDE_LINE_WIDTH,
        resolution: rendererSize,
        depthTest: false,
        transparent: true,
        opacity: 0.9,
      });
      this.guideGeometry = new LineSegmentsGeometry();
      this.guideLine = new LineSegments2(this.guideGeometry, this.guideMaterial);
      this.guideLine.renderOrder = GHOST_RENDER_ORDER;
      this.guideLine.frustumCulled = false;
      overlay.add(this.guideLine);
    }
    const origin = guides[0].a;
    const local: number[] = [];
    for (const guide of guides) {
      local.push(guide.a.x - origin.x, guide.a.y - origin.y, z, guide.b.x - origin.x, guide.b.y - origin.y, z);
    }
    this.guideGeometry?.dispose();
    this.guideGeometry = new LineSegmentsGeometry();
    this.guideGeometry.setPositions(local);
    this.guideLine.geometry = this.guideGeometry;
    this.guideLine.position.set(origin.x, origin.y, 0);
    this.guideLine.computeLineDistances();
    this.guideLine.visible = true;
  }

  private hideGuides(): void {
    if (this.guideLine) this.guideLine.visible = false;
  }

  private disposeOverlay(): void {
    if (this.overlay) {
      this.viewer.uiScene.remove(this.overlay);
      this.overlay = null;
    }
    this.ghostGeometry?.dispose();
    this.ghostMaterial?.dispose();
    this.ghostLine = null;
    this.ghostGeometry = null;
    this.ghostMaterial = null;
    this.spanGeometry?.dispose();
    this.spanMaterial?.dispose();
    this.spanLine = null;
    this.spanGeometry = null;
    this.spanMaterial = null;
    this.guideGeometry?.dispose();
    this.guideMaterial?.dispose();
    this.guideLine = null;
    this.guideGeometry = null;
    this.guideMaterial = null;
    this.openingHighlight.dispose();
    this.hoverMarker = null; // SphereMarker children are disposed with the group
  }

  // ─── Placement / selection ────────────────────────────────────────────

  /**
   * While armed (and not mid-placement), what already sits under the cursor —
   * a placed object or door / window — so it can be selected and moved
   * without leaving the tool. The dimension tool's wall snaps win: a click on
   * a wall corner / face still starts a measurement.
   */
  private placedUnderCursor(event: MouseEvent): { object: PointoramaSketchObject } | { opening: PointoramaOpeningSketch } | null {
    if (!this.armed || this.hasPendingPlacement()) return null;
    if (this.libraryKind === 'dimensions' && this.snapFreeRunPointInfo(event)?.onWall) return null;
    const mousePx = this.hitTester.mousePx(event);
    const picked = pickSketchObjectAtPixel(this.viewer, mousePx, this.hitTester, GLYPH_PICK_RADIUS_PX);
    if (picked) return { object: picked.object };
    const skeleton = this.getWallSketch();
    const openingId = skeleton ? pickOpeningSketchAtPixel(this.viewer, skeleton, mousePx, this.hitTester) : null;
    const opening = openingId ? this.viewer.scene.openingSketches.find((o) => o.identifier === openingId) : null;
    return opening ? { opening } : null;
  }

  public onClick(event: MouseEvent): void {
    if (event.button === 1) return; // middle-click = camera pan
    if (this.flipInteraction.handleClick()) return;
    // Gizmo handle click (sub-threshold) / trailing click of a gizmo drag.
    if (this.gizmoInteraction.handleClick(event)) return;
    if (this.selectedDrag.consumedClick()) return; // trailing click of a move/resize/slide drag

    // Wall-needing placement over an imagined wall: persist the transient
    // edge so the placement logic below operates on a real, saved wall.
    // (No-op after the onMouseDown commit — the edge is real by then.)
    this.commitImaginedWallEdge(event);
    // A plain click on a resize handle (no drag) shouldn't fall through to
    // the empty-space deselect OR place another object — the handles sit off
    // the glyph and stay live while armed. The controller restores the
    // selection suppression set on the handle mousedown.
    if (!this.hasPendingPlacement() && this.selectedDrag.handleClick(event)) return;

    // Armed, on something already placed: select it instead of placing.
    const placed = this.placedUnderCursor(event);
    if (placed) {
      const additive = event.ctrlKey || event.metaKey || event.shiftKey;
      if ('object' in placed) this.selectSketchObject(placed.object, additive);
      else this.viewer.scene.dispatchEvent({ type: 'measurement_select', value: { measure: placed.opening, isCtrl: additive } });
      return;
    }

    // Armed door/window: place an OPENING_SKETCH on the wall centerline —
    // fixed-length single click, or click-click free-draw on the same edge.
    if (this.armedOpening) {
      const skeleton = this.getWallSketch();
      if (!skeleton) return;
      const hit = this.snapOpeningWall(event);
      if (!hit) {
        this.pendingOpeningStart = null;
        this.hideSpan();
        return;
      }
      if (this.openingSettings.fixedLength) {
        const offset = clampOpeningOffsetToClearSpan(skeleton, hit.edgeId, this.openingSettings.length, hit.offset - this.openingSettings.length / 2);
        this.createOpeningFromSpan(skeleton, hit.edgeId, offset, this.openingSettings.length);
        return;
      }
      if (!this.pendingOpeningStart || this.pendingOpeningStart.edgeId !== hit.edgeId) {
        this.pendingOpeningStart = hit; // start (or restart on the new wall)
        return;
      }
      // Both ends held inside the corner-clear span — a click on/past a
      // corner node lands the opening flush with the crossing wall's face.
      const span = openingClearSpanOnEdge(skeleton, hit.edgeId);
      const lo = Math.max(span.start, Math.min(this.pendingOpeningStart.offset, hit.offset));
      const hi = Math.min(span.end, Math.max(this.pendingOpeningStart.offset, hit.offset));
      this.pendingOpeningStart = null;
      this.hideSpan();
      if (hi - lo < MIN_OPENING_LENGTH) return;
      this.createOpeningFromSpan(skeleton, hit.edgeId, lo, hi - lo);
      return;
    }

    if (this.armedTypeId) {
      const type = getSketchObjectType(this.armedTypeId);
      if (!type) return;

      // Free two-point runs (beams): click-click anywhere — the wall-editor
      // draw UX. Endpoints snap onto nearby walls (smart connect); a free end
      // additionally snaps the beam direction to 90° steps of the start wall.
      if (type.drawLikeWall) {
        // The click of the press that picked a wall face: its ends are set, the next click places the line
        // (after a drag, the line is already placed: nothing left to do).
        if (this.faceDimensionPress) {
          this.faceDimensionPress = false;
          return;
        }
        // Dimension, third click: the line lands at the pulled offset.
        if (this.pendingDimension) {
          const pulled = this.resolveDimensionOffset(type, event);
          if (!pulled) return;
          this.pendingDimension = null;
          this.hideMarker();
          this.hideGhost();
          this.createSketchObject(type, pulled.placement, { ...this.nextDimensionParams.get(type.id), [DIMENSION_OFFSET_PARAM]: pulled.offset });
          return;
        }
        if (!this.pendingFreeRunStart) {
          const info = this.snapFreeRunPointInfo(event);
          if (!info) return;
          this.pendingFreeRunStart = info.point;
          this.freeRunStartDir = info.edgeDir;
          return;
        }
        const ends = this.resolveRunEnds(type, event, this.pendingFreeRunStart);
        if (!ends) return;
        const placement = resolveFreeRun(type, ends.start, ends.end, this.getFloorZ());
        if (!placement || placement.frame.width < MIN_RUN_LENGTH) return;
        this.pendingFreeRunStart = null;
        this.freeRunStartDir = null;
        this.hideSpan();
        this.hideGhost();
        this.finishFreeRun(type, ends.start, ends.end, placement);
        return;
      }

      // Runs (Revit-style): a click ON a wall drops the run start, a second
      // click on the same wall commits the span; a click on a different wall
      // restarts there. Off any wall: mid-draw cancels the start, otherwise
      // WALL-only types do nothing while hybrid FREE types STAMP at the
      // cursor like any free object.
      if (this.isRunDrawType(type)) {
        const hit = this.snapWall(event);
        if (!hit) {
          if (this.pendingRunStart) {
            this.pendingRunStart = null;
            this.hideSpan();
            this.hideGhost();
            return;
          }
          if (type.constraint.kind === 'WALL') return;
          const strategy = getConstraintStrategy(type.constraint.kind);
          const placement = strategy.resolveHover(this.placementContext(event), type, this.rotation);
          if (!placement) return;
          this.createSketchObject(type, placement);
          return;
        }
        // A second click on a COLINEAR continuation edge commits across the
        // T-junction (remapped into the start edge's along-line coordinate);
        // a genuinely different wall restarts the run there.
        const skeleton = this.getWallSketch();
        const mapped = this.pendingRunStart && skeleton ? remapHitToEdgeLine(skeleton, this.pendingRunStart.edgeId, hit) : null;
        if (!this.pendingRunStart || !mapped) {
          this.pendingRunStart = hit;
          return;
        }
        const placement = this.resolveRunSpan(event, type, this.pendingRunStart, mapped);
        if (!placement || placement.frame.width < MIN_RUN_LENGTH) return;
        this.pendingRunStart = null;
        this.hideSpan();
        this.hideGhost();
        this.createSketchObject(type, placement);
        return;
      }

      // Re-resolve at the click position (the hover placement may be stale).
      const strategy = getConstraintStrategy(type.constraint.kind);
      const placement = strategy.resolveHover(this.placementContext(event), type, this.rotation);
      if (!placement) return;
      this.createSketchObject(type, placement);
      return;
    }

    const picked = pickSketchObjectAtPixel(this.viewer, this.hitTester.mousePx(event), this.hitTester, GLYPH_PICK_RADIUS_PX);
    // Ctrl/Cmd/Shift all accumulate a multi-selection.
    const additive = event.ctrlKey || event.metaKey || event.shiftKey;
    if (picked) {
      this.selectSketchObject(picked.object, additive);
      return;
    }
    // Opening sketches select "like the rest" — glyph or footprint line.
    const skeleton = this.getWallSketch();
    const pickedOpeningId = skeleton ? pickOpeningSketchAtPixel(this.viewer, skeleton, this.hitTester.mousePx(event), this.hitTester) : null;
    if (pickedOpeningId) {
      const opening = this.viewer.scene.openingSketches.find((o) => o.identifier === pickedOpeningId);
      if (opening) {
        this.viewer.scene.dispatchEvent({ type: 'measurement_select', value: { measure: opening, isCtrl: additive } });
        return;
      }
    }
    // A click inside a beam square selects the beam (see pickBeamByFootprint).
    const beam = this.pickBeamByFootprint(event);
    if (beam) {
      this.selectSketchObject(beam, additive);
      return;
    }
    // Clicked empty space → clear the selection. With a modifier held the
    // handler treats it as a no-op, so a missed modifier-click never wipes
    // an accumulating multi-selection.
    this.viewer.scene.dispatchEvent({ type: 'measurement_select', value: { measure: null, isCtrl: additive } });
    this.contextMenu?.hide();
  }

  public onRightClick(event: MouseEvent): void {
    // Armed: right-click cancels — first an in-progress placement start, then
    // the armed item itself (mirrors ESC).
    if (this.armed) {
      if (this.pendingRunStart || this.pendingFreeRunStart || this.pendingDimension || this.pendingOpeningStart) {
        this.clearPendingPlacement();
        this.hideSpan();
        this.hideGhost();
        if (this.lastHoverEvent && this.armedTypeId) this.refreshGhost(this.lastHoverEvent);
        else if (this.lastHoverEvent && this.armedOpening) this.refreshOpeningPreview(this.lastHoverEvent);
        return;
      }
      this.disarm();
      return;
    }

    const picked = pickSketchObjectAtPixel(this.viewer, this.hitTester.mousePx(event), this.hitTester, GLYPH_PICK_RADIUS_PX);
    let target = picked?.object ?? null;
    if (!target) {
      // Opening sketches get the same right-click delete menu as objects.
      const skeleton = this.getWallSketch();
      const openingId = skeleton ? pickOpeningSketchAtPixel(this.viewer, skeleton, this.hitTester.mousePx(event), this.hitTester) : null;
      const opening = openingId ? this.viewer.scene.openingSketches.find((o) => o.identifier === openingId) : null;
      if (opening && skeleton) {
        if (!opening.selected) this.viewer.scene.dispatchEvent({ type: 'measurement_select', value: { measure: opening, isCtrl: false } });
        const footprint = openingFootprint(skeleton, opening.identifier);
        if (!footprint) return;
        if (!this.contextMenu) this.contextMenu = new ContextMenu(this.viewer);
        const openingTargets = opening.selected ? this.selectedOperableOpenings() : [opening];
        this.contextMenu.show(
          [
            {
              id: 'delete',
              iconSvg: ICON_TRASH_SVG,
              tooltip: openingTargets.length > 1 ? this.viewer.translationService.t('delete ({count})', { args: { count: openingTargets.length } }) : this.viewer.translationService.t('delete'),
              closeOnClick: true,
              onClick: () => {
                for (const target of openingTargets) this.deleteOpeningSketch(target);
                this.getWallSketch()?.updateSkinningVisualization();
              },
            },
          ],
          footprint.a.clone().add(footprint.b).multiplyScalar(0.5),
        );
        return;
      }
      // A beam square under the cursor right-clicks like its outline.
      target = this.pickBeamByFootprint(event);
      if (!target) {
        // Right-click on empty space → clear the selection.
        this.viewer.scene.dispatchEvent({ type: 'measurement_select', value: { measure: null, isCtrl: false } });
        this.contextMenu?.hide();
        return;
      }
    }
    // Right-clicking a member of a multi-selection keeps that selection and
    // offers the batch delete; an unselected object is selected first (replace)
    // and gets the single delete, as before.
    if (!target.selected) this.selectSketchObject(target, false);
    const box = target.getBoundingBox();
    if (!box) return;
    if (!this.contextMenu) this.contextMenu = new ContextMenu(this.viewer);
    const targets = target.selected ? this.selectedOperableObjects() : [target];
    this.contextMenu.show(
      [
        {
          id: 'delete',
          iconSvg: ICON_TRASH_SVG,
          tooltip: targets.length > 1 ? this.viewer.translationService.t('delete ({count})', { args: { count: targets.length } }) : this.viewer.translationService.t('delete'),
          closeOnClick: true,
          onClick: () => {
            for (const object of targets) this.deleteSketchObject(object);
          },
        },
      ],
      box.getCenter(new THREE.Vector3()),
    );
  }

  private selectSketchObject(object: PointoramaSketchObject, isCtrl: boolean): void {
    this.viewer.scene.dispatchEvent({
      type: 'measurement_select',
      value: { measure: object, isCtrl },
    });
  }

  // ─── Box select ──────────────────────────────────────────────────────

  private ensureBoxSelect(): BoxSelectController {
    if (!this.boxSelect) {
      this.boxSelect = new BoxSelectController(this.viewer, {
        boxStyle: {
          border: '1.5px dashed #00ddff',
          background: 'rgba(0, 221, 255, 0.08)',
        },
      });
    }
    return this.boxSelect;
  }

  /** Objects whose glyph crosses the box go into the Redux selection with the
   *  drag's mode (replace / Shift-add / Ctrl-remove). Same visibility guards
   *  as picking, so hidden/other-floor objects never box-select. An empty
   *  `replace` box clears the selection, like every marquee. */
  private commitBoxSelect(mode: BoxSelectMode, rect: BoxRect): void {
    const identifiers: string[] = [];
    for (const object of this.viewer.scene.sketchObjects) {
      if (!object.visible || isAnnotationPickBlocked(this.viewer, object, 'SKETCH_OBJECT')) continue;
      const segs = object.getWorldGlyphSegments();
      let inside = false;
      for (let i = 0; i + 5 < segs.length; i += 6) {
        const a = this.hitTester.projectWorldToPixel({ x: segs[i], y: segs[i + 1], z: segs[i + 2] });
        const b = this.hitTester.projectWorldToPixel({ x: segs[i + 3], y: segs[i + 4], z: segs[i + 5] });
        if (!a || !b) continue;
        if (PixelHitTester.segmentIntersectsRect(a, b, rect.minX, rect.minY, rect.maxX, rect.maxY)) {
          inside = true;
          break;
        }
      }
      if (inside) identifiers.push(object.identifier);
    }
    // Opening sketches box-select alongside objects — footprint polyline vs
    // the rectangle, exactly as the OpeningSketchDrawTool's marquee.
    const skeleton = this.getWallSketch();
    if (skeleton) {
      for (const opening of this.viewer.scene.openingSketches) {
        if (opening.groupIdentifier !== skeleton.groupIdentifier || isAnnotationPickBlocked(this.viewer, opening, 'OPENING_SKETCH')) continue;
        const footprint = openingFootprint(skeleton, opening.identifier);
        if (!footprint) continue;
        let prev: THREE.Vector2 | null = null;
        let inside = false;
        for (const p of footprint.pts) {
          const px = this.hitTester.projectWorldToPixel(p);
          if (!px) {
            prev = null;
            continue;
          }
          if (prev && PixelHitTester.segmentIntersectsRect(prev, px, rect.minX, rect.minY, rect.maxX, rect.maxY)) {
            inside = true;
            break;
          }
          prev = px;
        }
        if (inside) identifiers.push(opening.identifier);
      }
    }
    this.viewer.scene.dispatchEvent({ type: 'annotations_box_select', value: { identifiers, mode } });
  }

  public onKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Escape' && this.boxSelect?.isActive()) {
      event.preventDefault();
      event.stopPropagation();
      this.boxSelect.cancel();
      this.dragOrbitBlocked = false;
      this.viewer.inputHandler.unblockOrbitControls();
      return;
    }
    if (event.key === 'Escape' && this.armed) {
      event.preventDefault();
      event.stopPropagation();
      // First ESC cancels an in-progress placement start; the next disarms.
      if (this.pendingRunStart || this.pendingFreeRunStart || this.pendingDimension || this.pendingOpeningStart) {
        this.clearPendingPlacement();
        this.hideSpan();
        this.hideGhost();
        if (this.lastHoverEvent && this.armedTypeId) this.refreshGhost(this.lastHoverEvent);
        else if (this.lastHoverEvent && this.armedOpening) this.refreshOpeningPreview(this.lastHoverEvent);
        return;
      }
      this.disarm();
      return;
    }
    if (event.key === 'r' || event.key === 'R') {
      // Openings take their orientation from the wall — nothing to rotate,
      // and the key must not rotate the (unrelated) selection behind the ghost.
      if (this.armedOpening) return;
      const direction: 1 | -1 = event.shiftKey ? -1 : 1;
      // Key repeat = the key is being held: rotate smoothly until release and
      // drop the pending tap step so a hold never starts with a 45° jump.
      if (event.repeat) {
        this.pendingRotateTap = null;
        this.startRotateHold(direction);
        return;
      }
      // Don't step yet — whether this is a tap (step 45° on release) or a
      // hold (rotate smoothly) is only known once repeat/keyup arrives.
      this.pendingRotateTap = direction;
      return;
    }
    if ((event.key === 'Delete' || event.key === 'Backspace') && !this.armed) {
      const selected = this.selectedOperableObjects();
      const selectedOpenings = this.selectedOperableOpenings();
      if (selected.length === 0 && selectedOpenings.length === 0) return;
      event.preventDefault();
      for (const object of selected) this.deleteSketchObject(object);
      for (const opening of selectedOpenings) this.deleteOpeningSketch(opening);
      // Deleted openings leave stale holes in the skin — one authoritative re-cut.
      if (selectedOpenings.length > 0) this.getWallSketch()?.updateSkinningVisualization();
    }
  }

  /** While a FREE-placement ghost is armed, a plain scroll rotates it
   *  (5° per notch) — in 2D AND 3D, so placement feels identical in both.
   *  Everything else keeps its navigation meaning:
   *   - wall-attached types take their orientation from the wall, so there is
   *     nothing to rotate and the wheel is never captured;
   *   - ctrl+scroll stays the ZOOM gesture while placing (it passes through
   *     to the camera in both modes) — only the plain scroll is repurposed.
   *  Idle wheel always passes through to the camera. */
  public onWheel(event: WheelEvent): void {
    if (!this.armedTypeId) return;
    const type = getSketchObjectType(this.armedTypeId);
    if (!type || type.constraint.kind !== 'FREE') return;
    if (event.ctrlKey) return;
    event.preventDefault();
    event.stopPropagation();
    const direction = event.deltaY > 0 ? 1 : -1;
    this.rotation += direction * ROTATE_WHEEL_STEP;
    if (this.lastHoverEvent) this.refreshGhost(this.lastHoverEvent);
  }

  public onKeyUp(event: KeyboardEvent): void {
    if (event.key !== 'r' && event.key !== 'R') return;
    if (this.armedOpening) return;
    this.endRotateHold();
    if (this.pendingRotateTap === null) return;
    const direction = this.pendingRotateTap;
    this.pendingRotateTap = null;
    if (this.armedTypeId) {
      this.rotation += direction * ROTATE_STEP;
      if (this.lastHoverEvent) this.refreshGhost(this.lastHoverEvent);
    } else {
      this.rotateSelectedObjects(direction * ROTATE_STEP);
    }
  }

  // ─── Smooth rotation while `R` is held ────────────────────────────────

  private startRotateHold(direction: 1 | -1): void {
    if (this.rotateHold) {
      this.rotateHold.direction = direction;
      return;
    }
    let before: Map<string, { type: RegisteredSketchObjectType; before: PlacementValues }> | null = null;
    if (!this.armedTypeId) {
      before = new Map();
      for (const object of this.selectedOperableObjects()) {
        const type = object.getType();
        if (!type || type.constraint.kind !== 'FREE') continue;
        before.set(object.identifier, { type, before: capturePlacementValues(object.identifier) });
      }
      if (before.size === 0) return;
    }
    this.rotateHold = { direction, lastTime: performance.now(), before };
    this.viewer.addEventListener('update', this.boundRotateHoldTick);
  }

  private rotateHoldTick(): void {
    const hold = this.rotateHold;
    if (!hold) return;
    const now = performance.now();
    const step = hold.direction * ROTATE_HOLD_SPEED * ((now - hold.lastTime) / 1000);
    hold.lastTime = now;
    if (this.armedTypeId) {
      this.rotation += step;
      if (this.lastHoverEvent) this.refreshGhost(this.lastHoverEvent);
    } else {
      this.rotateSelectedObjects(step, false);
    }
  }

  /** Stop the held rotation and push the whole sweep as one undo step. */
  private endRotateHold(): void {
    const hold = this.rotateHold;
    if (!hold) return;
    this.rotateHold = null;
    this.viewer.removeEventListener('update', this.boundRotateHoldTick);
    if (!hold.before) return;
    for (const [id, { type, before }] of hold.before) {
      this.undoRedo?.pushState(this.createMoveSnapshot(id, type, before, capturePlacementValues(id)));
    }
  }

  /**
   * Rotate every selected FREE object by `step` radians around its own
   * center. LINE-anchored objects (stairs) rotate their anchor points; the
   * rotation property is kept in sync with the new anchor direction. Wall-
   * attached objects can't rotate (their orientation is the wall's).
   * `pushUndo: false` is the held-rotation path — the per-frame steps are
   * collapsed into a single undo snapshot when the key is released.
   */
  private rotateSelectedObjects(step: number, pushUndo = true): void {
    for (const object of this.selectedOperableObjects()) {
      const type = object.getType();
      if (!type || type.constraint.kind !== 'FREE') continue;
      const id = object.identifier;
      const before = capturePlacementValues(id);
      if (!before.anchor || before.anchor.length === 0) continue;

      const cos = Math.cos(step);
      const sin = Math.sin(step);
      let cx = 0;
      let cy = 0;
      for (const p of before.anchor) {
        cx += p.x;
        cy += p.y;
      }
      cx /= before.anchor.length;
      cy /= before.anchor.length;
      const anchor: Coordinate[] = before.anchor.map((p) => ({
        x: cx + (p.x - cx) * cos - (p.y - cy) * sin,
        y: cy + (p.x - cx) * sin + (p.y - cy) * cos,
        z: p.z,
      }));
      const rotation = anchor.length >= 2 ? Math.atan2(anchor[1].y - anchor[0].y, anchor[1].x - anchor[0].x) : before.rotation + step;

      const after: PlacementValues = { anchor, rotation, wall: null };
      getConstraintStrategy('FREE').writePlacement(id, after);
      if (pushUndo) this.undoRedo?.pushState(this.createMoveSnapshot(id, type, before, after));
    }
  }

  // ─── Move (drag) ──────────────────────────────────────────────────────

  public onMouseDown(event: MouseEvent): void {
    if (event.button !== 0) return;
    this.boxSelectArmed = false;
    this.armPressInteractions(event);
    // A press that armed a tool interaction can only become a tool gesture —
    // block orbit RIGHT NOW rather than at the drag threshold: waiting loses
    // the race against the camera controls, which rotate during the first
    // pixels and keep drifting on their damped deltas (same reasoning as the
    // wall editor's empty-press orbit block). Released in onMouseUp (which
    // fires after onDragEnd on every release) or onDisable. The flip/gizmo
    // interactions returned before arming anything and manage their own block.
    if (this.boxSelectArmed || this.selectedDrag.isArmed() || this.openingDrawDrag || this.runDrawDrag || this.freeRunDrag || this.faceDimensionPress) {
      this.dragOrbitBlocked = true;
      this.viewer.inputHandler.blockOrbitControls();
    }
  }

  /** Arm whichever press interaction the mousedown hits (gizmo/handle grab,
   *  object move, opening slide, drag-to-draw start, or the empty-space box
   *  select) — split out so onMouseDown can apply the orbit block after ANY
   *  arming branch returned. */
  private armPressInteractions(event: MouseEvent): void {
    // A mousedown on a flip gizmo arms the flip and consumes the interaction.
    if (this.flipInteraction.handleMouseDown(event)) return;

    // The transform gizmo beats every other grab — ALSO while a library
    // item is armed: it rides the just-placed object so placing and
    // adjusting is one flow. (No binding resolves mid multi-click/drag-draw
    // placement, so rubber-band flows are never hijacked.) Checked before
    // the imagined-edge commit: grabbing a handle must not persist a
    // transient wall the interaction never uses.
    if (this.gizmoInteraction.handleMouseDown(event)) return;

    // Run-resize end handles stay live while armed too (grow a just-placed
    // run from one side right away) — a handle grab beats placement.
    if (!this.hasPendingPlacement() && this.tryArmResizeHandleDrag(event)) return;

    // Armed, on something already placed: grab it like the idle tool does —
    // a drag moves it, a plain click selects it (onClick).
    const placed = this.placedUnderCursor(event);
    if (placed) {
      if ('opening' in placed) {
        this.selectedDrag.armOpeningSlide(event);
        return;
      }
      const type = placed.object.getType();
      if (type?.drawLikeWall) this.armBeamBodyDrag(placed.object, event);
      else if (type) this.selectedDrag.armObjectBodyDrag(placed.object, type, event);
      return;
    }

    // Wall-needing placement over an imagined wall: persist the transient
    // edge so every flow below operates on a real, saved wall.
    this.commitImaginedWallEdge(event);

    // Armed door/window: mousedown over a wall arms drag-to-draw (free-draw
    // only — fixed-length placement is a single click, handled in onClick).
    if (this.armedOpening) {
      this.openingDrawDrag = null;
      if (!this.openingSettings.fixedLength && !this.pendingOpeningStart) {
        this.openingDrawDrag = this.snapOpeningWall(event);
      }
      return;
    }

    if (this.armedTypeId) {
      // Runs support drag-to-draw (mousedown + drag = the span, committed on
      // drag-end). A plain click without a real drag falls through to the
      // click-click flow in onClick — like the opening tool. Free runs
      // (beams, drawLikeWall) start anywhere; wall runs need a wall.
      this.runDrawDrag = null;
      this.freeRunDrag = null;
      this.faceDimensionPress = false;
      const type = getSketchObjectType(this.armedTypeId);
      if (type?.drawLikeWall) {
        // (While a dimension waits for its offset click, a press is that click.)
        if (!this.pendingFreeRunStart && !this.pendingDimension) {
          const info = this.snapFreeRunPointInfo(event);
          if (type.dimension && info?.face) {
            // A press on a wall face measures the whole face: drag out the line, or click and place it.
            this.pendingDimension = { start: info.face.a, end: info.face.b };
            this.faceDimensionPress = true;
            this.hideSpan();
            return;
          }
          this.freeRunDrag = info?.point ?? null;
          this.freeRunStartDir = info?.edgeDir ?? null;
        }
      } else if (type && this.isRunDrawType(type) && !this.pendingRunStart) {
        this.runDrawDrag = this.snapWall(event);
      }
      return;
    }

    this.selectedDrag.cancel();
    const mousePx = this.hitTester.mousePx(event);

    // A resize handle beats grabbing the object under it.
    if (this.tryArmResizeHandleDrag(event)) return;

    const picked = pickSketchObjectAtPixel(this.viewer, mousePx, this.hitTester, GLYPH_PICK_RADIUS_PX);
    if (!picked) {
      // An opening sketch under the cursor arms a SLIDE along its wall (no
      // prior selection required — mirrors the OpeningSketchDrawTool).
      if (this.selectedDrag.armOpeningSlide(event)) return;
      // A beam square under the cursor (interior/centerline, not just the
      // dashed outline) grabs the whole beam.
      const beam = this.pickBeamByFootprint(event);
      if (beam) {
        this.armBeamBodyDrag(beam, event);
        return;
      }
      // Empty space → a left-drag from here is a rubber-band box select (the
      // AreaSelector only appears once the drag threshold confirms it's a
      // drag, see onDragStart). 2D ortho views only — in 3D a left-drag is
      // orbit navigation (same restriction as the wall editor's
      // isInteractiveViewActive).
      this.boxSelectArmed = this.viewer.scene.cameraMode === CameraMode.ORTHOGRAPHIC;
      return;
    }
    const type = picked.object.getType();
    if (!type) return;
    // Beams keep their two-point anchor through a move, so the regular
    // constraint-driven body drag (which would re-center and wall-align the
    // run) doesn't apply — grabbing the body arms a whole-beam translation
    // instead (both endpoints re-sticking to nearby walls; Shift/Ctrl moves
    // it exactly as grabbed).
    if (type.drawLikeWall) {
      this.armBeamBodyDrag(picked.object, event);
      return;
    }
    this.selectedDrag.armObjectBodyDrag(picked.object, type, event);
  }

  /** The drag threshold was just crossed — if the mousedown armed a box
   *  select (hit nothing), start the rubber-band now. Starting here rather
   *  than on mousedown keeps sub-threshold clicks from flashing a phantom
   *  rectangle (same reasoning as the door/window tool). */
  public onDragStart(drag: DragEventData): void {
    if (this.flipInteraction.handleDragStart()) return;
    // An armed gizmo handle starts its drag (translate/rotate/scale — the
    // binding writes the placement/size properties, the wrapper the undo).
    if (this.gizmoInteraction.handleDragStart(drag)) return;
    if (!this.boxSelectArmed || drag.button !== 0) return;
    this.boxSelectArmed = false;
    this.ensureBoxSelect().start(drag.event);
  }

  /** Release the press-time orbit block (see onMouseDown) once the gesture
   *  resolves — fires on every left release, drag and plain click alike
   *  (after onDragEnd committed a real drag). */
  public onMouseUp(event: MouseEvent): void {
    if (event.button !== 0) return;
    // A press that never became a drag leaves its drag-to-draw start behind
    // (onDragEnd, which runs first on a real drag, already committed it):
    // drop it, or it reads as a placement still in flight — the gizmo stays
    // unbound and the rubber band keeps following from the last click.
    this.freeRunDrag = null;
    this.runDrawDrag = null;
    this.openingDrawDrag = null;
    if (this.dragOrbitBlocked) {
      this.dragOrbitBlocked = false;
      this.viewer.inputHandler.unblockOrbitControls();
    }
  }

  public onDrag(drag: DragEventData): void {
    if (drag.button !== 0) return;

    // A flip-gizmo handle drag moves the opening (offset / inset).
    if (this.flipInteraction.handleDrag(drag.event)) return;

    if (this.gizmoInteraction.handleDrag(drag)) return;

    if (this.boxSelect?.isActive()) {
      this.boxSelect.update(drag.event);
      return;
    }

    // Drag-drawing an opening span: rubber-band on the start edge (both ends
    // held inside the corner-clear span, like the commit).
    if (this.openingDrawDrag) {
      const skeleton = this.getWallSketch();
      if (!skeleton) return;
      const end = cursorOffsetOnOpeningEdge(drag.event, this.viewer, skeleton, this.openingDrawDrag.edgeId);
      if (end === null) return;
      const span = openingClearSpanOnEdge(skeleton, this.openingDrawDrag.edgeId);
      const s0 = Math.max(span.start, Math.min(span.end, this.openingDrawDrag.offset));
      const s1 = Math.max(span.start, Math.min(span.end, end));
      const pts = openingSpanPolyline(skeleton, this.openingDrawDrag.edgeId, s0, s1);
      if (pts) {
        this.showMarker(pts[pts.length - 1]);
        this.showSpanPolyline(pts);
      }
      return;
    }

    // Body drags / handle resizes of the selected object (opening slide,
    // object move, run/beam resize) — shared controller (undo + hole re-cut
    // + trailing-click swallow included).
    if (this.selectedDrag.handleDrag(drag)) return;

    // A wall face pressed for a dimension: the drag pulls its line off the face.
    if (this.faceDimensionPress && this.pendingDimension) {
      this.refreshGhost(drag.event);
      return;
    }

    // Drag-drawing a free run (beam): rubber-band between the pressed point
    // and the cursor — end wall-snapped, or 90°-direction-snapped when free.
    if (this.freeRunDrag) {
      const type = this.armedTypeId ? getSketchObjectType(this.armedTypeId) : null;
      const ends = type ? this.resolveRunEnds(type, drag.event, this.freeRunDrag) : null;
      if (!type || !ends) return;
      this.showMarker(ends.end);
      this.showSpan(ends.start, ends.end);
      const placement = resolveFreeRun(type, ends.start, ends.end, this.getFloorZ());
      if (placement && placement.frame.width >= MIN_RUN_LENGTH) this.showGhost(type.withParams(this.nextDimensionParams.get(type.id) ?? {}).glyph(placement.frame), placement.frame);
      else this.hideGhost();
      return;
    }

    // Drag-drawing a wall run: rubber-band the span on the start edge.
    if (this.runDrawDrag) {
      const skeleton = this.getWallSketch();
      const geom = skeleton ? wallEdgeGeometry(skeleton, this.runDrawDrag.edgeId) : null;
      if (!skeleton || !geom) return;
      const end = this.cursorOffsetOnEdge(drag.event, skeleton.getFloorHeight(), geom);
      if (end === null) return;
      const z = skeleton.getFloorHeight();
      const endPt = this.edgeWorldPoint(geom, end, z);
      this.showMarker(endPt);
      this.showSpan(this.edgeWorldPoint(geom, this.runDrawDrag.offset, z), endPt);
      const type = this.armedTypeId ? getSketchObjectType(this.armedTypeId) : null;
      if (type) {
        const endHit: WallEdgeHit = { edgeId: this.runDrawDrag.edgeId, offset: end, point: endPt };
        this.showRunPreview(type, drag.event, this.runDrawDrag, endHit);
      }
      return;
    }

    // Resize a run by its end handle — the far end stays pinned, the dragged
    // end follows the cursor (glyph skinning reflows live through the
    // property subscriptions). WALL runs slide along their wall edge; FREE
    // runs (stairs) move the grabbed anchor endpoint along the run axis.
  }

  public onDragEnd(drag: DragEventData): void {
    // Commit a box select: hit-test every operable object against the
    // rectangle and apply the captured mode to the Redux selection. (The
    // orbit block is released by onMouseUp, which fires right after.)
    if (this.boxSelect?.isActive()) {
      const result = this.boxSelect.finish();
      if (result) this.commitBoxSelect(result.mode, result.rect);
      return;
    }

    // Finish a flip-gizmo handle drag: commit/undo handled by the wrapper.
    if (this.flipInteraction.handleDragEnd()) return;

    // Finish a gizmo drag: commit/undo/trailing-click handled by the wrapper.
    if (this.gizmoInteraction.handleDragEnd(drag)) return;

    // Commit a drag-drawn opening span.
    if (this.openingDrawDrag) {
      const start = this.openingDrawDrag;
      this.openingDrawDrag = null;
      this.hideSpan();
      const skeleton = this.getWallSketch();
      if (!skeleton) return;
      const end = cursorOffsetOnOpeningEdge(drag.event, this.viewer, skeleton, start.edgeId);
      if (end === null) return;
      const span = openingClearSpanOnEdge(skeleton, start.edgeId);
      const lo = Math.max(span.start, Math.min(start.offset, end));
      const hi = Math.min(span.end, Math.max(start.offset, end));
      if (hi - lo < MIN_OPENING_LENGTH) return;
      this.createOpeningFromSpan(skeleton, start.edgeId, lo, hi - lo);
      return;
    }

    // Commit a body/handle drag of the selected object (opening slide,
    // object move, run/beam resize) — shared controller.
    if (this.selectedDrag.handleDragEnd()) return;

    // A wall face dragged out: the dimension lands where the line was let go
    // (`faceDimensionPress` stays set so the trailing click does nothing).
    if (this.faceDimensionPress && this.pendingDimension) {
      const type = this.armedTypeId ? getSketchObjectType(this.armedTypeId) : null;
      const pulled = type ? this.resolveDimensionOffset(type, drag.event) : null;
      if (!type || !pulled) return;
      this.pendingDimension = null;
      this.hideMarker();
      this.hideGhost();
      this.createSketchObject(type, pulled.placement, { ...this.nextDimensionParams.get(type.id), [DIMENSION_OFFSET_PARAM]: pulled.offset });
      return;
    }

    // Commit a drag-drawn free run (beam).
    if (this.freeRunDrag) {
      const start = this.freeRunDrag;
      this.freeRunDrag = null;
      this.hideSpan();
      this.hideGhost();
      const type = this.armedTypeId ? getSketchObjectType(this.armedTypeId) : null;
      const ends = type ? this.resolveRunEnds(type, drag.event, start) : null;
      this.freeRunStartDir = null;
      if (!type || !ends) return;
      const placement = resolveFreeRun(type, ends.start, ends.end, this.getFloorZ());
      if (!placement || placement.frame.width < MIN_RUN_LENGTH) return;
      this.finishFreeRun(type, ends.start, ends.end, placement);
      return;
    }

    // Commit a drag-drawn wall run.
    if (this.runDrawDrag) {
      const start = this.runDrawDrag;
      this.runDrawDrag = null;
      this.hideSpan();
      this.hideGhost();
      const type = this.armedTypeId ? getSketchObjectType(this.armedTypeId) : null;
      const skeleton = this.getWallSketch();
      const geom = skeleton ? wallEdgeGeometry(skeleton, start.edgeId) : null;
      if (!type || !skeleton || !geom) return;
      const end = this.cursorOffsetOnEdge(drag.event, skeleton.getFloorHeight(), geom);
      if (end === null) return;
      const endHit: WallEdgeHit = { edgeId: start.edgeId, offset: end, point: this.edgeWorldPoint(geom, end, skeleton.getFloorHeight()) };
      const placement = this.resolveRunSpan(drag.event, type, start, endHit);
      if (!placement || placement.frame.width < MIN_RUN_LENGTH) return;
      this.createSketchObject(type, placement);
      return;
    }

  }

  // ─── Create / delete + undo ───────────────────────────────────────────

  /** `params` overrides param defaults (a dimension's offset). */
  private createSketchObject(type: RegisteredSketchObjectType, placement: ResolvedPlacement, params: CustomParamValues = {}): void {
    const groupIdentifier = this.currentFloorGroupIdentifier;
    if (!groupIdentifier) return;

    // Mint the id here so the undo snapshot can re-create the same object.
    // Width/depth come from the resolved frame: for normal placements that's
    // the type's default size; for drawn wall runs it's the drawn span.
    const request: SketchObjectRequest = {
      identifier: `sketch_object_${groupIdentifier}_${uuid()}`,
      groupIdentifier,
      typeId: type.id,
      width: placement.frame.width,
      depth: placement.frame.depth,
      anchor: placement.anchor,
      rotation: placement.rotation,
      wall: placement.wall,
      paramValues: [
        ...type.initialParamProperties().map((param) => {
          const key = Object.keys(params).find((k) => param.identifier === paramPropertyIdentifier(type.id, k));
          return key === undefined ? param : { ...param, value: params[key] };
        }),
        // A dimension's length text, as set in the tool menu.
        ...(type.dimension
          ? [
              { identifier: 'sketchobject_dimension_decimals', value: this.nextDimensionText.decimals, valueType: PropertyValueType.INT },
              { identifier: 'sketchobject_dimension_show_unit', value: this.nextDimensionText.showUnit, valueType: PropertyValueType.BOOLEAN },
              { identifier: 'sketchobject_dimension_text_along_line', value: this.nextDimensionText.alongLine, valueType: PropertyValueType.BOOLEAN },
              { identifier: 'sketchobject_dimension_text_size', value: this.nextDimensionText.size, valueType: PropertyValueType.FLOAT },
            ]
          : []),
      ],
    };
    // Select the object as soon as it lands in the scene (see
    // handleSketchObjectAdded) so its values are immediately visible and
    // editable in the properties panel.
    this.pendingAutoSelectId = request.identifier;
    this.viewer.scene.dispatchEvent({ type: 'request_sketch_object', value: request });
    this.undoRedo?.pushState(this.createToggleSnapshot(request, true));
  }

  /** Identifier of a freshly-requested object to auto-select on arrival. */
  private pendingAutoSelectId: string | null = null;

  /** Auto-select a just-placed object once React has created its scene object.
   *  Scoped to the exact identifier minted in {@link createSketchObject}, so
   *  objects appearing for other reasons (sync, undo/redo re-create) don't
   *  steal the selection. */
  private handleSketchObjectAdded = (event: { sketchObject?: PointoramaSketchObject }): void => {
    const object = event.sketchObject;
    if (!object || !this.pendingAutoSelectId || object.identifier !== this.pendingAutoSelectId) return;
    this.pendingAutoSelectId = null;
    this.selectSketchObject(object, false);
  };

  private deleteSketchObject(object: PointoramaSketchObject): void {
    const request = this.captureRequest(object);
    this.viewer.scene.dispatchEvent({ type: 'measurement_delete', value: { measure: object } });
    if (request) this.undoRedo?.pushState(this.createToggleSnapshot(request, false));
  }

  /**
   * Existence-toggle snapshot shared by create and delete. `exists` is the
   * state right after the action: each restore() flips between deleting the
   * object and re-creating it (same identifier + captured params).
   */
  private createToggleSnapshot(request: SketchObjectRequest, existsInitially: boolean): Snapshot {
    let exists = existsInitially;
    const viewer = this.viewer;
    const snapshot: Snapshot = {
      restore: (): Snapshot => {
        if (exists) {
          const obj = viewer.scene.sketchObjects.find((o) => o.identifier === request.identifier);
          if (obj) viewer.scene.dispatchEvent({ type: 'measurement_delete', value: { measure: obj } });
        } else {
          viewer.scene.dispatchEvent({ type: 'request_sketch_object', value: request });
        }
        exists = !exists;
        return snapshot;
      },
    };
    return snapshot;
  }

  private createMoveSnapshot(objectId: string, type: RegisteredSketchObjectType, before: PlacementValues, after: PlacementValues): Snapshot {
    return createPlacementMoveSnapshot(objectId, type, before, after);
  }

  /** Rebuild a full re-create request from an existing object's properties. */
  private captureRequest(object: PointoramaSketchObject): SketchObjectRequest | null {
    const groupIdentifier = object.groupIdentifier;
    const type = object.getType();
    if (!groupIdentifier || !type) return null;
    const id = object.identifier;
    const service = getGlobalPropertyService();
    return {
      identifier: id,
      groupIdentifier,
      typeId: type.id,
      width: getSketchObjectWidthProperty(id).value ?? type.defaultSize.width,
      depth: getSketchObjectDepthProperty(id).value ?? type.defaultSize.depth,
      ...capturePlacementValues(id),
      paramValues: type.initialParamProperties().map((param) => {
        const raw = service.getProperty(param.identifier, id, PropertyType.PROJECT_GLOBAL).value;
        return { ...param, value: typeof raw === typeof param.value ? (raw as number | string | boolean) : param.value };
      }),
    };
  }
}

/** Unit frame at the origin sized to the type's default footprint (thumbnails). */
function thumbnailFrame(type: RegisteredSketchObjectType): SketchObjectFrame {
  return {
    center: { x: 0, y: 0 },
    dir: { x: 1, y: 0 },
    norm: { x: 0, y: 1 },
    width: type.defaultSize.width,
    depth: type.defaultSize.depth,
    z: 0,
  };
}
